import React, { useState, useEffect, useRef } from 'react';
import { formatDate } from '../utils/dateUtils';
import {
  Search,
  Download,
  Calendar,
  ChevronLeft,
  ChevronRight,
  Pencil,
  Check,
  X
} from 'lucide-react';

const InvoiceReportComponent = () => {
  const [loading, setLoading] = useState(false);
  const [loadingStep, setLoadingStep] = useState({ message: '', progress: 0 });
  const [invoices, setInvoices] = useState([]);
  const [allCombinedData, setAllCombinedData] = useState([]);
  const [creditNotes, setCreditNotes] = useState([]);
  const [customerData, setCustomerData] = useState({});
  const [summaryTotals, setSummaryTotals] = useState({
    totalBeforeVat: 0,
    totalVat: 0,
    grandTotal: 0,
    totalRecords: 0
  });
  const [allInvoicesForSummary, setAllInvoicesForSummary] = useState([]);
  
  // Advanced caching system with TTL and memory management
  const [customerCache] = useState(() => {
    const CACHE_TTL = 30 * 60 * 1000; // 30 minutes
    const MAX_CACHE_SIZE = 1000; // Maximum number of cached customers
    
    return {
      data: new Map(),
      
      get(custNo) {
        const entry = this.data.get(custNo);
        if (!entry) return null;
        
        // Check if entry has expired
        if (Date.now() > entry.expiry) {
          this.data.delete(custNo);
          return null;
        }
        
        // Update last accessed time
        entry.lastAccessed = Date.now();
        return entry.value;
      },
      
      set(custNo, value) {
        // Clean up if cache is too large
        if (this.data.size >= MAX_CACHE_SIZE) {
          this.cleanup();
        }
        
        const expiry = Date.now() + CACHE_TTL;
        this.data.set(custNo, {
          value,
          expiry,
          lastAccessed: Date.now(),
          created: Date.now()
        });
      },
      
      has(custNo) {
        return this.get(custNo) !== null;
      },
      
      delete(custNo) {
        return this.data.delete(custNo);
      },
      
      cleanup() {
        const now = Date.now();
        const entries = Array.from(this.data.entries());
        
        // Remove expired entries
        entries.forEach(([key, entry]) => {
          if (now > entry.expiry) {
            this.data.delete(key);
          }
        });
        
        // If still too large, remove oldest accessed entries
        if (this.data.size >= MAX_CACHE_SIZE) {
          const sortedEntries = Array.from(this.data.entries())
            .sort(([,a], [,b]) => a.lastAccessed - b.lastAccessed);
          
          const toRemove = this.data.size - Math.floor(MAX_CACHE_SIZE * 0.8);
          for (let i = 0; i < toRemove; i++) {
            this.data.delete(sortedEntries[i][0]);
          }
        }
        
        console.log(`Cache cleaned up, ${this.data.size} entries remaining`);
      },
      
      clear() {
        this.data.clear();
      },
      
      getStats() {
        const now = Date.now();
        const entries = Array.from(this.data.values());
        
        return {
          size: this.data.size,
          expired: entries.filter(entry => now > entry.expiry).length,
          avgAge: entries.length > 0 ? 
            entries.reduce((sum, entry) => sum + (now - entry.created), 0) / entries.length / 1000 / 60 : 0 // minutes
        };
      }
    };
  });
  const [loadingCustomers, setLoadingCustomers] = useState(false);
  
  // Enhanced PDF export states
  const [pdfLoading, setPdfLoading] = useState(false);
  const [pdfProgress, setPdfProgress] = useState({
    show: false,
    step: '',
    current: 0,
    total: 0,
    percentage: 0
  });
  const [pagination, setPagination] = useState({
    page: 1,
    limit: 20,
    total: 0,
    pages: 0
  });
  
  // Inline edit customer name states
  const [customerNameOverrides, setCustomerNameOverrides] = useState({});
  const [editingRow, setEditingRow] = useState(null);
  const [editingValue, setEditingValue] = useState('');
  const editInputRef = useRef(null);

  // Inline edit tax ID states
  const [taxIdOverrides, setTaxIdOverrides] = useState({});
  const [editingTaxIdRow, setEditingTaxIdRow] = useState(null);
  const [editingTaxIdValue, setEditingTaxIdValue] = useState('');
  const editTaxIdInputRef = useRef(null);

  const handleStartEdit = (invoiceKey, currentName, e) => {
    e.stopPropagation();
    setEditingRow(invoiceKey);
    setEditingValue(currentName);
    setTimeout(() => editInputRef.current?.focus(), 50);
  };

  const handleConfirmEdit = (invoiceKey) => {
    if (editingValue.trim()) {
      setCustomerNameOverrides(prev => ({ ...prev, [invoiceKey]: editingValue.trim() }));
    }
    setEditingRow(null);
    setEditingValue('');
  };

  const handleCancelEdit = () => {
    setEditingRow(null);
    setEditingValue('');
  };

  const handleStartEditTaxId = (invoiceKey, currentTaxId, e) => {
    e.stopPropagation();
    setEditingTaxIdRow(invoiceKey);
    setEditingTaxIdValue(currentTaxId);
    setTimeout(() => editTaxIdInputRef.current?.focus(), 50);
  };

  const handleConfirmEditTaxId = (invoiceKey) => {
    if (editingTaxIdValue.trim()) {
      setTaxIdOverrides(prev => ({ ...prev, [invoiceKey]: editingTaxIdValue.trim() }));
    }
    setEditingTaxIdRow(null);
    setEditingTaxIdValue('');
  };

  const handleCancelEditTaxId = () => {
    setEditingTaxIdRow(null);
    setEditingTaxIdValue('');
  };

  // Invoice details popup states
  const [showInvoiceDetails, setShowInvoiceDetails] = useState(false);
  const [selectedInvoice, setSelectedInvoice] = useState(null);
  const [invoiceDetails, setInvoiceDetails] = useState([]);
  const [loadingDetails, setLoadingDetails] = useState(false);
  
  // Filter states
  const [filters, setFilters] = useState({
    month: new Date().getMonth() + 1,
    year: new Date().getFullYear(),
    prefix: '1'
  });

  const prefixOptions = [
    { value: '1', label: '1: ขายสด' },
    { value: '2', label: '2: ขายเชื่อ' },
    { value: '3', label: '3: บริการสด' },
    { value: '4', label: '4: บริการเชื่อ' },
    { value: '5', label: '5: เครื่องในประกัน' },
    { value: '6', label: '6: รายได้อื่นๆ' }
  ];

  const monthOptions = [
    { value: 1, label: 'มกราคม' },
    { value: 2, label: 'กุมภาพันธ์' },
    { value: 3, label: 'มีนาคม' },
    { value: 4, label: 'เมษายน' },
    { value: 5, label: 'พฤษภาคม' },
    { value: 6, label: 'มิถุนายน' },
    { value: 7, label: 'กรกฎาคม' },
    { value: 8, label: 'สิงหาคม' },
    { value: 9, label: 'กันยายน' },
    { value: 10, label: 'ตุลาคม' },
    { value: 11, label: 'พฤศจิกายน' },
    { value: 12, label: 'ธันวาคม' }
  ];

  const generateYearOptions = () => {
    const currentYear = new Date().getFullYear();
    const years = [];
    for (let i = currentYear - 5; i <= currentYear + 1; i++) {
      years.push({ value: i, label: i.toString() });
    }
    return years;
  };

  const formatNumber = (number) => {
    if (number == null) return '0.00';
    return parseFloat(number).toLocaleString('th-TH', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    });
  };

  const calculateBeforeVat = (totalAmount, vatAmount) => {
    return (totalAmount || 0) - (vatAmount || 0);
  };

  // ฟังก์ชันสำหรับแปลง BranchNo ให้เป็น 5 หลัก
  const formatBranchNo = (branchNo) => {
    const num = parseInt(branchNo || 0);
    return num.toString().padStart(5, '0');
  };

  const calculateSummaryTotals = (invoiceList) => {
    let totalBeforeVat = 0;
    let totalVat = 0;
    let grandTotal = 0;

    invoiceList.forEach(invoice => {
      const beforeVat = calculateBeforeVat(invoice.TotalAmount, invoice.Vat_Amount);
      const vat = parseFloat(invoice.Vat_Amount || 0);
      const total = parseFloat(invoice.TotalAmount || 0);

      totalBeforeVat += beforeVat;
      totalVat += vat;
      grandTotal += total;
    });

    return {
      totalBeforeVat: totalBeforeVat,
      totalVat: totalVat,
      grandTotal: grandTotal,
      totalRecords: invoiceList.length
    };
  };

  useEffect(() => {
    const summaryData = calculateSummaryTotals(allInvoicesForSummary);
    setSummaryTotals(summaryData);
  }, [allInvoicesForSummary]);

  const getDateRange = (month, year) => {
    // วันแรกของเดือน
    const startDate = new Date(year, month - 1, 1);
    // วันสุดท้ายของเดือน (เดือนถัดไป วันที่ 0)
    const endDate = new Date(year, month, 0);
    
    // แปลงเป็น ISO DateTime format สำหรับ API
    // DateFrom: เริ่มต้นวัน (00:00:00.000Z)
    const dateFromISO = `${year}-${month.toString().padStart(2, '0')}-01T00:00:00.000Z`;
    
    // DateTo: สิ้นสุดวัน (23:59:59.999Z)  
    const dateToISO = `${year}-${month.toString().padStart(2, '0')}-${endDate.getDate().toString().padStart(2, '0')}T23:59:59.999Z`;
    
    // 🗓️ DEBUG: แสดงข้อมูลวันที่ที่คำนวณได้
    console.log('🗓️ getDateRange DEBUG:', {
      input: { 
        month: `${month} (${month === 2 ? 'February' : 'Month ' + month})`, 
        year 
      },
      calculations: {
        startDate: startDate.toDateString(),
        endDate: endDate.toDateString(),
        lastDayOfMonth: endDate.getDate(),
        isFebruary: month === 2,
        isLeapYear: year % 4 === 0
      },
      output: { 
        dateFrom: `${dateFromISO} (วันที่ 1 เวลา 00:00:00)`, 
        dateTo: `${dateToISO} (วันที่ ${endDate.getDate()} เวลา 23:59:59)`,
        totalDaysInMonth: endDate.getDate()
      },
      summary: `ขอข้อมูลตั้งแต่ ${dateFromISO} ถึง ${dateToISO} (รวม ${endDate.getDate()} วัน)`,
      formatNote: 'ใช้ ISO DateTime format สำหรับ API'
    });
    
    return {
      dateFrom: dateFromISO,
      dateTo: dateToISO
    };
  };

  // Helper function to format invoice number with * for cancelled invoices
  const formatInvoiceNumber = (invoice) => {
    const invNumber = invoice.Inv_Number?.trim() || '';
    // Check if invoice is cancelled (Open_Close = true)
    if (invoice.Open_Close === true || invoice.Open_Close === 'true' || invoice.Open_Close === 1) {
      return `*${invNumber}`;
    }
    return invNumber;
  };

  const fetchInvoices = async (page = 1) => {
    setLoading(true);
    setLoadingStep({ message: 'กำลังดึงข้อมูลใบแจ้งหนี้...', progress: 15 });
    try {
      const { dateFrom, dateTo } = getDateRange(filters.month, filters.year);
      
      const PAGE_LIMIT = 20;
      const requestBody = {
        page: 1,
        limit: 999999,
        CompanyCode: process.env.REACT_APP_COMPANY_CODE,
        prefix: filters.prefix,
        DateFrom: dateFrom,
        DateTo: dateTo,
        sortBy: "Inv_Date,Inv_Number",
        sortOrder: "ASC"
      };
      
      // 📝 DEBUG: แสดง request body ที่ส่งไป API
      console.log('📝 fetchInvoices REQUEST DEBUG:', {
        endpoint: `${process.env.REACT_APP_API_BASE_URL}${process.env.REACT_APP_INVOICE_ENDPOINT}`,
        requestBody,
        filters: filters,
        currentPage: page
      });
      
      
      const response = await fetch(`${process.env.REACT_APP_API_BASE_URL}${process.env.REACT_APP_INVOICE_ENDPOINT}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(requestBody)
      });

      const result = await response.json();
      
      // 📨 DEBUG: แสดง API response
      console.log('📨 fetchInvoices RESPONSE DEBUG:', {
        status: result.status,
        dataExists: !!result.data,
        invoiceCount: result.data?.invoices?.length || 0,
        pagination: result.data?.pagination,
        firstInvoice: result.data?.invoices?.[0],
        lastInvoice: result.data?.invoices?.[result.data?.invoices?.length - 1]
      });
      
      if (result.status && result.data) {
        const invoiceList = result.data.invoices || [];

        // Fetch customer data for all invoices first, then set invoices
        setLoadingStep({ message: `กำลังโหลดข้อมูลลูกค้า (${invoiceList.length} รายการ)...`, progress: 45 });
        if (invoiceList.length > 0) {
          await fetchAllCustomerDataSimple(invoiceList);
        }

        setLoadingStep({ message: 'กำลังดึง Credit Notes...', progress: 75 });
        // Sort invoices by date first, then by invoice number (ascending) - FORCED SORT
        const sortedInvoices = [...invoiceList].sort((a, b) => {
          const dateA = new Date(a.Inv_Date).getTime();
          const dateB = new Date(b.Inv_Date).getTime();
          if (dateA !== dateB) return dateA - dateB;
          const numA = parseInt((a.Inv_Number?.trim() || '').replace(/\D/g, '')) || 0;
          const numB = parseInt((b.Inv_Number?.trim() || '').replace(/\D/g, '')) || 0;
          return numA - numB;
        });

        // ดึงข้อมูล Credit Notes
        const creditNotesData = await fetchCreditNotesData(dateFrom, dateTo);
        setCreditNotes(creditNotesData);

        setLoadingStep({ message: 'กำลังจัดเรียงข้อมูล...', progress: 90 });
        // Combine invoices with credit notes (client-side pagination)
        const combinedData = combineInvoicesAndCreditNotes(sortedInvoices, creditNotesData);
        setAllCombinedData(combinedData);

        // Set pagination based on combined total
        setPagination({
          page: 1,
          limit: PAGE_LIMIT,
          total: combinedData.length,
          pages: Math.ceil(combinedData.length / PAGE_LIMIT)
        });

        // Show first page
        setInvoices(combinedData.slice(0, PAGE_LIMIT));
      } else {
        console.error('API Error:', result);
        setInvoices([]);
        setAllCombinedData([]);
        setPagination({ page: 1, limit: 20, total: 0, pages: 0 });
      }
    } catch (error) {
      console.error('Fetch error:', error);
      setInvoices([]);
      setPagination({ page: 1, limit: 20, total: 0, pages: 0 });
    } finally {
      setLoading(false);
    }
  };

  const fetchAllInvoicesForSummary = async () => {
    try {
      const { dateFrom, dateTo } = getDateRange(filters.month, filters.year);
      
      const requestBody = {
        page: 1,
        limit: 999999, // ดึงข้อมูลทั้งหมด
        CompanyCode: process.env.REACT_APP_COMPANY_CODE,
        prefix: filters.prefix,
        DateFrom: dateFrom,
        DateTo: dateTo
      };

      // 📊 DEBUG: แสดง request สำหรับ summary
      console.log('📊 fetchAllInvoicesForSummary REQUEST DEBUG:', {
        purpose: 'Loading all invoices for summary calculation',
        requestBody,
        isFullDataRequest: true
      });

      const response = await fetch(`${process.env.REACT_APP_API_BASE_URL}${process.env.REACT_APP_INVOICE_ENDPOINT}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(requestBody)
      });

      const result = await response.json();
      
      // 📊 DEBUG: แสดง response สำหรับ summary
      const summaryInvoices = result.data?.invoices || [];
      const summaryDates = summaryInvoices.map(inv => inv.Inv_Date).filter(Boolean);
      const uniqueSummaryDates = [...new Set(summaryDates)].sort();
      
      console.log('📊 fetchAllInvoicesForSummary RESPONSE DEBUG:', {
        status: result.status,
        totalInvoicesLoaded: summaryInvoices.length,
        dateRange: `${uniqueSummaryDates[0]} ถึง ${uniqueSummaryDates[uniqueSummaryDates.length - 1]}`,
        feb28InSummary: uniqueSummaryDates.includes('2026-02-28'),
        summaryInvoicesWithDate28: summaryInvoices.filter(inv => inv.Inv_Date === '2026-02-28').length,
        allUniqueDates: uniqueSummaryDates
      });

      if (result.status && result.data?.invoices) {
        setAllInvoicesForSummary(result.data.invoices);
        console.log(`✅ Loaded ${result.data.invoices.length} invoices for summary calculation`);
      } else {
        console.error('All invoices summary API Error:', result);
        setAllInvoicesForSummary([]);
      }
    } catch (error) {
      console.error('Fetch all invoices for summary error:', error);
      setAllInvoicesForSummary([]);
    }
  };

  // Fetch Credit Notes
  const fetchCreditNotes = async () => {
    try {
      const { dateFrom, dateTo } = getDateRange(filters.month, filters.year);
      
      const requestBody = {
        page: 1,
        limit: 200,
        CompanyCode: process.env.REACT_APP_COMPANY_CODE,
        DateFrom: dateFrom,
        DateTo: dateTo
      };

      // 💳 DEBUG: แสดง request สำหรับ Credit Notes
      console.log('💳 fetchCreditNotes REQUEST DEBUG:', {
        purpose: 'Loading Credit Notes for the selected period',
        requestBody,
        isoCreditNotesFormat: true,
        endpoint: 'https://apisaleeasy.psisat.com/api/cn/search'
      });

      const response = await fetch('https://apisaleeasy.psisat.com/api/cn/search', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(requestBody)
      });

      const result = await response.json();
      
      // 💳 DEBUG: แสดง response สำหรับ Credit Notes
      const creditNotesList = result.data?.credit_notes || [];
      const cnDates = creditNotesList.map(cn => cn.CN_Date).filter(Boolean);
      const uniqueCnDates = [...new Set(cnDates)].sort();
      
      console.log('💳 fetchCreditNotes RESPONSE DEBUG:', {
        status: result.status,
        totalCreditNotesLoaded: creditNotesList.length,
        dateRange: uniqueCnDates.length > 0 ? `${uniqueCnDates[0]} ถึง ${uniqueCnDates[uniqueCnDates.length - 1]}` : 'ไม่มีข้อมูล',
        feb28InCreditNotes: uniqueCnDates.includes('2026-02-28'),
        creditNotesWithDate28: creditNotesList.filter(cn => cn.CN_Date === '2026-02-28').length,
        allUniqueDates: uniqueCnDates,
        sampleCreditNotes: creditNotesList.slice(0, 3).map(cn => ({
          CN_Number: cn.CN_Number?.trim(),
          CN_Date: cn.CN_Date,
          Inv_Number: cn.Inv_Number?.trim(),
          TotalAmount: cn.TotalAmount
        }))
      });

      if (result.status && result.data?.credit_notes) {
        console.log(`✅ Loaded ${result.data.credit_notes.length} credit notes`);
        return result.data.credit_notes;
      } else {
        console.error('Credit Notes API Error:', result);
        return [];
      }
    } catch (error) {
      console.error('Fetch credit notes error:', error);
      return [];
    }
  };

  // แยก fetchCreditNotes ออกเป็น 2 function:
  // 1. fetchCreditNotesData - return ข้อมูลเพื่อใช้ใน Promise.all
  const fetchCreditNotesData = async (dateFromParam, dateToParam) => {
    try {
      const requestBody = {
        page: 1,
        limit: 200,
        CompanyCode: process.env.REACT_APP_COMPANY_CODE,
        DateFrom: dateFromParam,
        DateTo: dateToParam
      };
      
      // 💳 DEBUG: แสดง request สำหรับ Credit Notes
      console.log('💳 fetchCreditNotesData REQUEST DEBUG:', {
        purpose: 'Loading Credit Notes for fetchInvoices call',
        requestBody,
        endpoint: 'https://apisaleeasy.psisat.com/api/cn/search'
      });
      
      const response = await fetch('https://apisaleeasy.psisat.com/api/cn/search', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(requestBody)
      });
      const result = await response.json();
      
      if (result.status && result.data?.credit_notes) {
        console.log(`✅ fetchCreditNotesData loaded ${result.data.credit_notes.length} credit notes`);
        return result.data.credit_notes;
      } else {
        console.error('fetchCreditNotesData API Error:', result);
        return [];
      }
    } catch (error) {
      console.error('fetchCreditNotesData error:', error);
      return [];
    }
  };

  // 2. fetchCreditNotesLegacy - wrapper สำหรับการเรียกใช้แบบเดิม
  const fetchCreditNotesLegacy = async () => {
    const { dateFrom, dateTo } = getDateRange(filters.month, filters.year);
    const creditNotesData = await fetchCreditNotesData(dateFrom, dateTo);
    setCreditNotes(creditNotesData);
  };

  // Combine invoices and credit notes for display (Filter by selected prefix)
  const combineInvoicesAndCreditNotes = (invoiceList, creditNoteList) => {
    const combinedData = [];
    const selectedPrefix = filters.prefix;
    
    // Filter invoices to match the selected prefix
    const filteredInvoices = invoiceList.filter(invoice => {
      const invNumber = invoice.Inv_Number?.trim() || '';
      return invNumber.startsWith(selectedPrefix);
    });
    
    const prefixLabel = prefixOptions.find(p => p.value === selectedPrefix)?.label || `prefix ${selectedPrefix}`;
    console.log(`🔍 Filtered ${filteredInvoices.length} invoices (${prefixLabel}) from ${invoiceList.length} total invoices`);
    
    // Get all invoice numbers for the selected prefix to filter related CNs
    const filteredInvoiceNumbers = new Set(
      filteredInvoices.map(invoice => invoice.Inv_Number?.trim()).filter(Boolean)
    );
    
    // Convert filtered invoices to standard format
    filteredInvoices.forEach(invoice => {
      combinedData.push({
        ...invoice,
        type: 'invoice',
        displayDate: invoice.Inv_Date,
        displayNumber: invoice.Inv_Number,
        displayAmount: invoice.TotalAmount,
        displayCustomer: invoice.Cust_No
      });
    });
    
    // Filter credit notes - แสดง CN ทั้งหมดที่มี prefix ตรงกัน (ไม่ต้องเช็ค Invoice ในช่วงวันที่)
    const relatedCreditNotes = creditNoteList.filter(cn => {
      const relatedInvNumber = cn.Inv_Number?.trim() || '';
      // เช็คเฉพาะ prefix ของ Invoice ที่ CN อ้างอิง
      return relatedInvNumber.startsWith(selectedPrefix);
    });
    
    console.log(`🔍 Filtered ${relatedCreditNotes.length} credit notes with prefix "${selectedPrefix}" from ${creditNoteList.length} total CNs`);
    
    // DEBUG: แสดงข้อมูล CN ทั้งหมดสำหรับ prefix นี้
    if (creditNoteList.length > 0) {
      const currentPrefixCNs = creditNoteList.filter(cn => cn.Inv_Number?.trim()?.charAt(0) === selectedPrefix);
      console.log(`🏷️ Credit Notes ทั้งหมดสำหรับ prefix "${selectedPrefix}":`, currentPrefixCNs.map(cn => ({
        CN_Number: cn.CN_Number?.trim(),
        CN_Date: cn.CN_Date,
        relatedInv: cn.Inv_Number?.trim(),
        amount: cn.TotalAmount
      })));
      
      if (relatedCreditNotes.length === 0 && currentPrefixCNs.length > 0) {
        console.log(`⚠️ มี CN ${currentPrefixCNs.length} รายการสำหรับ prefix "${selectedPrefix}" แต่ไม่แสดงผล - ตรวจสอบการ filter`);
      }
    }
    
    // Convert filtered credit notes to standard format (negative amounts)
    relatedCreditNotes.forEach(cn => {
      // Determine CN type based on related invoice number first digit
      const relatedInvNumber = cn.Inv_Number?.trim() || '';
      let cnType = 'unknown';
      
      if (relatedInvNumber.length > 0) {
        const firstDigit = relatedInvNumber.charAt(0);
        switch (firstDigit) {
          case '1':
            cnType = 'type1';
            break;
          case '2':
            cnType = 'type2';
            break;
          case '3':
            cnType = 'type3';
            break;
          default:
            cnType = 'unknown';
        }
      }
      
      combinedData.push({
        ...cn,
        type: 'credit_note',
        cnType: cnType, // เพิ่ม field cnType เพื่อแยกประเภท CN
        displayDate: cn.CN_Date,
        displayNumber: cn.CN_Number,
        displayAmount: -cn.TotalAmount, // Make credit note amounts negative
        displayCustomer: cn.Cust_No,
        relatedInvoice: cn.Inv_Number, // Keep track of related invoice
        // Map CN fields to invoice-like structure for compatibility
        Inv_Date: cn.CN_Date,
        Inv_Number: cn.CN_Number,
        TotalAmount: -cn.TotalAmount,
        Cust_No: cn.Cust_No,
        Amount: -cn.Amount,
        Vat_Amount: -cn.Vat_Amount
      });
    });
    
    // Log CN type distribution for debugging
    const cnTypeCount = relatedCreditNotes.reduce((acc, cn) => {
      const relatedInvNumber = cn.Inv_Number?.trim() || '';
      const firstDigit = relatedInvNumber.charAt(0);
      const type = firstDigit === '1' ? 'type1' : firstDigit === '2' ? 'type2' : firstDigit === '3' ? 'type3' : 'unknown';
      acc[type] = (acc[type] || 0) + 1;
      return acc;
    }, {});
    
    console.log(`📊 Combined data: ${filteredInvoices.length} invoices + ${relatedCreditNotes.length} CNs = ${combinedData.length} total records`);
    console.log(`🏷️ CN Type distribution:`, cnTypeCount);
    
    // Sort combined data by date and then by document number
    return combinedData.sort((a, b) => {
      // First sort by date
      const dateA = new Date(a.displayDate).getTime();
      const dateB = new Date(b.displayDate).getTime();
      
      if (dateA !== dateB) {
        return dateA - dateB;
      }
      
      // Same date - sort by document number
      const numA = a.displayNumber?.trim() || '';
      const numB = b.displayNumber?.trim() || '';
      
      return numA.localeCompare(numB);
    });
  };

  // Fetch invoice details
  const fetchInvoiceDetails = async (invoiceNumber) => {
    setLoadingDetails(true);
    try {
      const response = await fetch(`https://apisaleeasy.psisat.com/api/inv/045/${invoiceNumber}/details`);
      
      if (!response.ok) {
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      }

      const result = await response.json();
      
      if (result.status && result.data && result.data.details) {
        setInvoiceDetails(result.data.details);
      } else {
        throw new Error('Invalid API response');
      }
    } catch (error) {
      console.error('Error fetching invoice details:', error);
      setInvoiceDetails([]);
    } finally {
      setLoadingDetails(false);
    }
  };

  // Handle row click
  const handleRowClick = (invoice) => {
    // Check if current user is pensri
    const storedUserData = localStorage.getItem('userData');
    const currentUser = storedUserData ? JSON.parse(storedUserData) : null;
    const userName = currentUser?.username || '';
    
    // Only allow pensri user to view invoice details
    if (userName.toLowerCase() !== 'pensri') {
      return;
    }
    
    setSelectedInvoice(invoice);
    setShowInvoiceDetails(true);
    fetchInvoiceDetails(invoice.Inv_Number?.trim());
  };

  // Memory usage monitoring
  const [memoryUsage, setMemoryUsage] = useState({ used: 0, total: 0, percentage: 0 });
  
  // API health monitoring
  const [apiHealth, setApiHealth] = useState({
    isHealthy: true,
    lastHealthCheck: Date.now(),
    responseTimeHistory: [],
    errorRate: 0,
    consecutiveFailures: 0,
    totalRequests: 0,
    totalErrors: 0
  });
  
  // Debug mode state
  const [debugMode] = useState(() => {
    // Enable debug mode in development or when URL contains debug=true
    return process.env.NODE_ENV === 'development' || 
           new URLSearchParams(window.location.search).get('debug') === 'true';
  });
  
  // Performance monitoring
  const updateApiHealth = (responseTime, success, errorType = null) => {
    setApiHealth(prev => {
      const newResponseTimes = [...prev.responseTimeHistory, responseTime].slice(-50); // Keep last 50 measurements
      const newTotalRequests = prev.totalRequests + 1;
      const newTotalErrors = prev.totalErrors + (success ? 0 : 1);
      const newErrorRate = (newTotalErrors / newTotalRequests) * 100;
      const newConsecutiveFailures = success ? 0 : prev.consecutiveFailures + 1;
      
      const avgResponseTime = newResponseTimes.reduce((sum, time) => sum + time, 0) / newResponseTimes.length;
      const isHealthy = newErrorRate < 20 && avgResponseTime < 10000 && newConsecutiveFailures < 3;
      
      const healthData = {
        isHealthy,
        lastHealthCheck: Date.now(),
        responseTimeHistory: newResponseTimes,
        errorRate: newErrorRate,
        consecutiveFailures: newConsecutiveFailures,
        totalRequests: newTotalRequests,
        totalErrors: newTotalErrors,
        avgResponseTime,
        errorType
      };
      
      if (debugMode) {
        console.log('API Health Update:', healthData);
      }
      
      return healthData;
    });
  };
  
  // Monitor memory usage if available
  useEffect(() => {
    const checkMemoryUsage = () => {
      if ('memory' in performance) {
        const mem = performance.memory;
        setMemoryUsage({
          used: Math.round(mem.usedJSHeapSize / 1024 / 1024), // MB
          total: Math.round(mem.totalJSHeapSize / 1024 / 1024), // MB
          percentage: Math.round((mem.usedJSHeapSize / mem.totalJSHeapSize) * 100)
        });
      }
    };
    
    const interval = setInterval(checkMemoryUsage, 5000); // Check every 5 seconds
    checkMemoryUsage(); // Initial check
    
    return () => clearInterval(interval);
  }, []);
  
  // Cleanup cache when memory usage is high
  useEffect(() => {
    if (memoryUsage.percentage > 80) {
      console.warn('High memory usage detected, cleaning up cache');
      customerCache.cleanup();
      
      // Force garbage collection if available (for development)
      if (typeof window !== 'undefined' && window.gc) {
        window.gc();
      }
    }
  }, [memoryUsage]);

  useEffect(() => {
    // 🔄 DEBUG: แสดงข้อมูล filters เมื่อมีการเปลี่ยนแปลง
    console.log('🔄 useEffect - FILTERS CHANGED:', {
      selectedMonth: filters.month,
      selectedYear: filters.year,
      selectedPrefix: filters.prefix,
      monthName: monthOptions.find(m => m.value === filters.month)?.label || 'ไม่ระบุ',
      action: 'filters เปลี่ยนแปลง - เริ่มดึงข้อมูลใหม่ทั้งหมด'
    });
    
    fetchInvoices(1); // Credit Notes จะถูกเรียกใน fetchInvoices แล้ว
    fetchAllInvoicesForSummary();
  }, [filters]);
  
  // Cleanup cache on component unmount
  useEffect(() => {
    return () => {
      customerCache.clear();
    };
  }, []);

  const handleSearch = () => {
    // 🔍 DEBUG: แสดงข้อมูล filters เมื่อกดค้นหา
    console.log('🔍 handleSearch - CURRENT FILTERS:', {
      selectedMonth: filters.month,
      selectedYear: filters.year,
      selectedPrefix: filters.prefix,
      monthName: monthOptions.find(m => m.value === filters.month)?.label || 'ไม่ระบุ',
      action: 'กดปุ่มค้นหา - เริ่มดึงข้อมูล'
    });
    
    fetchInvoices(1); // Credit Notes จะถูกเรียกใน fetchInvoices แล้ว
    fetchAllInvoicesForSummary();
  };

  const handlePageChange = (newPage) => {
    if (newPage >= 1 && newPage <= pagination.pages) {
      const limit = pagination.limit;
      const start = (newPage - 1) * limit;
      setInvoices(allCombinedData.slice(start, start + limit));
      setPagination(prev => ({ ...prev, page: newPage }));
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  };

  // Simple customer data loading (for table display)
  const fetchAllCustomerDataSimple = async (invoiceList) => {
    // Prevent duplicate calls
    if (fetchAllCustomerDataSimple.isRunning) {
      console.log('fetchAllCustomerDataSimple already running, skipping...');
      return;
    }
    
    fetchAllCustomerDataSimple.isRunning = true;
    
    try {
      // Fetch customer data for all invoices (including cash invoices)
      console.log('🔴 fetchAllCustomerDataSimple - Total invoices:', invoiceList.length);
      console.log('🔴 fetchAllCustomerDataSimple - All Type_Inv_Code values:', invoiceList.map(inv => ({ num: inv.Inv_Number?.trim(), code: inv.Type_Inv_Code })));
      console.log('🔴 fetchAllCustomerDataSimple - Sample invoice raw data (first invoice):', invoiceList[0]);
      console.log('🔴 Invoice properties:', Object.keys(invoiceList[0] || {}));
      
      const uniqueCustomerNumbers = [...new Set(invoiceList.map(invoice => invoice.Cust_No?.trim()).filter(Boolean))];

    // Helper function to fetch single customer with retry
    const fetchCustomerWithRetry = async (custNo, retries = 3) => {
      for (let attempt = 1; attempt <= retries; attempt++) {
        try {
          const response = await fetch(`${process.env.REACT_APP_API_BASE_URL}${process.env.REACT_APP_CUSTOMER_ENDPOINT}`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ custNo })
          });

          const result = await response.json();

          if (result.status && result.data) {
            const newCustomerInfo = {
              name: result.data.Cust_Name || 'ไม่ระบุ',
              taxCode: result.data.TaxCode || '-',
              branchNo: result.data.BranchNo || 0
            };
            setCustomerData(prev => ({ ...prev, [custNo]: newCustomerInfo }));
            customerCache.set(custNo, newCustomerInfo);
            return { success: true, custNo, data: newCustomerInfo };
          } else {
            throw new Error('Invalid API response');
          }
        } catch (error) {
          if (attempt === retries) {
            const defaultData = { name: 'ไม่ระบุ', taxCode: '-', branchNo: 0 };
            setCustomerData(prev => ({ ...prev, [custNo]: defaultData }));
            return { success: false, custNo, error: error.message };
          }
          await new Promise(resolve => setTimeout(resolve, attempt * 500));
        }
      }
    };

    // Parallel batch loading (30 concurrent requests per batch)
    const BATCH_SIZE = 30;
    const totalCustomers = uniqueCustomerNumbers.length;

    for (let i = 0; i < uniqueCustomerNumbers.length; i += BATCH_SIZE) {
      const batch = uniqueCustomerNumbers.slice(i, i + BATCH_SIZE);

      // Separate cached vs uncached in this batch
      const uncached = batch.filter(custNo => {
        if (customerCache.has(custNo)) {
          setCustomerData(prev => ({ ...prev, [custNo]: customerCache.get(custNo) }));
          return false;
        }
        if (customerData[custNo] && customerData[custNo].name !== 'ไม่ระบุ') {
          return false;
        }
        return true;
      });

      // Fire all uncached in batch concurrently
      if (uncached.length > 0) {
        await Promise.all(uncached.map(custNo => fetchCustomerWithRetry(custNo)));
      }

      // Update progress
      const loaded = Math.min(i + BATCH_SIZE, totalCustomers);
      setLoadingStep({ message: `กำลังโหลดข้อมูลลูกค้า (${loaded}/${totalCustomers})...`, progress: 45 + Math.round((loaded / totalCustomers) * 25) });
    }
    
    } catch (error) {
      console.error('Error in fetchAllCustomerDataSimple:', error);
    } finally {
      // Reset running flag
      fetchAllCustomerDataSimple.isRunning = false;
    }
  };

  // Enhanced parallel customer data loading with robust retry logic
  const fetchAllCustomerDataWithProgress = async (allInvoices) => {
    // Fetch customer data for all invoices (including cash invoices)
    const uniqueCustomerNumbers = [...new Set(allInvoices.map(invoice => invoice.Cust_No?.trim()).filter(Boolean))];
    const totalCustomers = uniqueCustomerNumbers.length;
    
    console.log(`PDF Export: Processing ${totalCustomers} unique customers from ${allInvoices.length} total invoices`);
    console.log(`Note: ${allInvoices.length - totalCustomers} invoices have duplicate customers (same customer with multiple invoices)`);
    
    let processedCustomers = 0;
    let successCount = 0;
    let failedCustomers = [];
    const results = new Map(); // Thread-safe results storage

    // Helper function to fetch customer with aggressive retry (5 attempts)
    const fetchCustomerWithRetry = async (custNo, maxRetries = 5) => {
      // Check if already loaded with valid data
      if (customerCache.has(custNo)) {
        const cachedData = customerCache.get(custNo);
        results.set(custNo, { success: true, custNo, data: cachedData, fromCache: true });
        return results.get(custNo);
      }

      // Check if already loaded in state with valid data (not default)
      if (customerData[custNo] && customerData[custNo].name !== 'ไม่ระบุ') {
        results.set(custNo, { success: true, custNo, data: customerData[custNo], fromState: true });
        return results.get(custNo);
      }

      // Aggressive retry with exponential backoff
      for (let attempt = 1; attempt <= maxRetries; attempt++) {
        try {
          const response = await fetch(`${process.env.REACT_APP_API_BASE_URL}${process.env.REACT_APP_CUSTOMER_ENDPOINT}`, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json'
            },
            body: JSON.stringify({
              custNo: custNo
            })
          });

          if (!response.ok) {
            throw new Error(`HTTP ${response.status}: ${response.statusText}`);
          }

          const result = await response.json();
          
          if (result.status && result.data) {
            const newCustomerInfo = {
              name: result.data.Cust_Name?.trim() || 'ไม่ระบุ',
              taxCode: result.data.TaxCode?.trim() || '-',
              branchNo: result.data.BranchNo || 0
            };
            
            // Cache the result immediately (thread-safe)
            customerCache.set(custNo, newCustomerInfo);
            results.set(custNo, { success: true, custNo, data: newCustomerInfo, attempts: attempt });
            
            return results.get(custNo);
          } else {
            throw new Error(`Invalid API response: ${JSON.stringify(result)}`);
          }
        } catch (error) {
          console.error(`Attempt ${attempt}/${maxRetries} failed for customer ${custNo}:`, error.message);
          
          if (attempt === maxRetries) {
            // Final failure after all retries - use default data
            const defaultData = { name: 'ไม่ระบุ', taxCode: '-', branchNo: 0 };
            results.set(custNo, { success: false, custNo, data: defaultData, error: error.message, attempts: attempt });
            return results.get(custNo);
          }
          
          // Exponential backoff: 500ms, 1s, 2s, 4s, 8s
          const delay = Math.min(500 * Math.pow(2, attempt - 1), 8000);
          await new Promise(resolve => setTimeout(resolve, delay));
        }
      }
    };

    // Update progress tracking function
    const updateProgress = () => {
      const currentProcessed = processedCustomers;
      const currentSuccess = successCount;
      const percentage = 50 + ((currentProcessed / totalCustomers) * 30);
      const successRate = currentProcessed > 0 ? (currentSuccess / currentProcessed) * 100 : 0;
      
      setPdfProgress(prev => ({
        ...prev,
        step: `โหลดข้อมูลลูกค้า ${currentProcessed}/${totalCustomers} (${successRate.toFixed(0)}% สำเร็จ)`,
        percentage: percentage
      }));
    };

    // PARALLEL PROCESSING - Process all customers simultaneously
    console.log('Starting parallel customer data loading...');
    
    const customerPromises = uniqueCustomerNumbers.map(async (custNo) => {
      const result = await fetchCustomerWithRetry(custNo);
      
      // Update counters atomically
      processedCustomers++;
      if (result.success) {
        successCount++;
      } else {
        failedCustomers.push({ custNo, error: result.error });
      }
      
      // Update progress periodically (throttled)
      if (processedCustomers % 3 === 0 || processedCustomers === totalCustomers) {
        updateProgress();
      }
      
      return result;
    });

    // Wait for ALL customer data to complete
    const allResults = await Promise.allSettled(customerPromises);
    
    // Process results and update state in batch for better performance
    const customerDataUpdates = {};
    
    allResults.forEach((promiseResult, index) => {
      const custNo = uniqueCustomerNumbers[index];
      
      if (promiseResult.status === 'fulfilled') {
        const result = promiseResult.value;
        customerDataUpdates[custNo] = result.data;
        
        if (result.fromCache) {
          console.log(`✓ Customer ${custNo}: Loaded from cache`);
        } else if (result.fromState) {
          console.log(`✓ Customer ${custNo}: Already in state`);
        } else if (result.success) {
          console.log(`✓ Customer ${custNo}: Loaded successfully (${result.attempts} attempts)`);
        } else {
          console.warn(`✗ Customer ${custNo}: Failed after 5 attempts - ${result.error}`);
        }
      } else {
        console.error(`✗ Customer ${custNo}: Promise rejected - ${promiseResult.reason}`);
        customerDataUpdates[custNo] = { name: 'ไม่ระบุ', taxCode: '-', branchNo: 0 };
        failedCustomers.push({ custNo, error: promiseResult.reason?.message || 'Promise rejected' });
      }
    });

    // Batch update state for better performance
    setCustomerData(prev => ({ 
      ...prev, 
      ...customerDataUpdates 
    }));

    // Force a complete state refresh to ensure all data is synced
    setTimeout(() => {
      setCustomerData(prev => ({ ...prev, ...customerDataUpdates }));
      
      // Debug: Log current state after update
      console.log('Customer data state after update:', Object.keys(customerDataUpdates));
      console.log('Sample customer data:', Object.entries(customerDataUpdates).slice(0, 5));
    }, 100);

    // Also refresh the main invoices display to pick up new customer data
    if (typeof fetchInvoices === 'function') {
      setTimeout(() => {
        // Trigger a refresh of the invoices table to show updated customer names
        setInvoices(prevInvoices => [...prevInvoices]);
      }, 200);
    }

    // Final summary
    setPdfProgress(prev => ({
      ...prev,
      step: `โหลดข้อมูลลูกค้าเสร็จสิ้น: ทั้งหมด ${totalCustomers} รายการ (สำเร็จ ${successCount}, ล้มเหลว ${failedCustomers.length})`,
      percentage: 80
    }));

    // Log summary
    console.log(`Customer loading complete: ${successCount}/${totalCustomers} successful`);
    console.log('Final customer data updates:', customerDataUpdates);
    if (failedCustomers.length > 0) {
      console.warn('Failed customers:', failedCustomers);
    }

    return {
      total: totalCustomers,
      success: successCount,
      failed: failedCustomers.length,
      failedCustomers
    };
  };

  const exportToPDF = async () => {
    try {
      // Show progress popup
      setPdfProgress({
        show: true,
        step: 'เริ่มต้นการสร้าง PDF...',
        current: 0,
        total: 100,
        percentage: 0
      });
      setPdfLoading(true);

      // Import pdf-lib
      const { PDFDocument, StandardFonts, rgb } = await import('pdf-lib');

      // Update progress
      setPdfProgress(prev => ({
        ...prev,
        step: 'กำลังดึงข้อมูลทั้งหมด...',
        percentage: 10
      }));
      const { dateFrom, dateTo } = getDateRange(filters.month, filters.year);
      
      // Load ALL data for export (all pages)
      let allInvoices = [];
      let currentPage = 1;
      let totalPages = 1;
      
      // Also load all credit notes
      let allCreditNotes = [];
      
      do {
        // Update progress for data fetching with total records info
        const totalRecords = totalPages > 1 ? `(ประมาณ ${totalPages * 100} รายการ)` : '';
        setPdfProgress(prev => ({
          ...prev,
          step: `กำลังดึงข้อมูลหน้า ${currentPage}${totalPages > 1 ? `/${totalPages}` : ''} ${totalRecords}...`,
          percentage: 10 + (currentPage / Math.max(totalPages, 1)) * 30 // 10-40%
        }));

        const exportRequestBody = {
          page: currentPage,
          limit: 100, // Use smaller chunks for better API stability
          CompanyCode: process.env.REACT_APP_COMPANY_CODE,
          prefix: filters.prefix,
          DateFrom: dateFrom,
          DateTo: dateTo,
          sortBy: "Inv_Date,Inv_Number",
          sortOrder: "ASC"
        };
        
        const response = await fetch(`${process.env.REACT_APP_API_BASE_URL}${process.env.REACT_APP_INVOICE_ENDPOINT}`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json'
          },
          body: JSON.stringify(exportRequestBody)
        });

        const result = await response.json();
        
        if (!result.status || !result.data || !result.data.invoices) {
          throw new Error('ไม่สามารถดึงข้อมูลได้');
        }

        // Add invoices from this page
        allInvoices = allInvoices.concat(result.data.invoices);
        
        // Update pagination info
        if (result.data.pagination) {
          totalPages = result.data.pagination.pages;
        }
        
        currentPage++;
      } while (currentPage <= totalPages);
      
      // Fetch all credit notes for PDF
      setPdfProgress(prev => ({
        ...prev,
        step: 'กำลังดึงข้อมูล Credit Notes...',
        percentage: 35
      }));
      
      try {
        const cnRequestBody = {
          page: 1,
          limit: 1000,
          CompanyCode: process.env.REACT_APP_COMPANY_CODE,
          DateFrom: dateFrom,
          DateTo: dateTo
        };
        
        const cnResponse = await fetch('https://apisaleeasy.psisat.com/api/cn/search', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json'
          },
          body: JSON.stringify(cnRequestBody)
        });
        
        const cnResult = await cnResponse.json();
        if (cnResult.status && cnResult.data?.credit_notes) {
          allCreditNotes = cnResult.data.credit_notes;
        }
      } catch (error) {
        console.error('Error fetching credit notes for PDF:', error);
      }
      
      // Calculate unique customers for progress display
      const uniqueCustomerCount = [...new Set(allInvoices.map(invoice => invoice.Cust_No?.trim()).filter(Boolean))].length;
      
      // Combine invoices and credit notes for PDF
      const combinedDataForPDF = combineInvoicesAndCreditNotes(allInvoices, allCreditNotes);
      
      // Update progress - invoice data collection complete with total count
      setPdfProgress(prev => ({
        ...prev,
        step: `ดึงข้อมูลเสร็จสิ้น: ${allInvoices.length} ใบแจ้งหนี้, ${allCreditNotes.length} Credit Notes จาก ${uniqueCustomerCount} ลูกค้า - กำลังโหลดข้อมูลลูกค้า...`,
        percentage: 50
      }));

      // Fetch customer data for all invoices with progress
      const customerLoadResult = await fetchAllCustomerDataWithProgress(allInvoices);
      
      // Show customer loading summary
      setPdfProgress(prev => ({
        ...prev,
        step: `ข้อมูลลูกค้าเสร็จสิ้น: ${customerLoadResult.success}/${customerLoadResult.total} ลูกค้า จาก ${allInvoices.length} ใบแจ้งหนี้`,
        percentage: 80
      }));

      // Wait a moment to show the summary and ensure state sync
      await new Promise(resolve => setTimeout(resolve, 800));

      // Update progress - starting PDF creation
      setPdfProgress(prev => ({
        ...prev,
        step: 'เริ่มต้นสร้าง PDF...',
        percentage: 85
      }));

      // Create PDF document with proper fontkit support
      const pdfDoc = await PDFDocument.create();
      
      // Import and register fontkit properly
      let fontkit;
      try {
        fontkit = await import('fontkit');
        // Try different ways to access fontkit
        const fontkitInstance = fontkit.default || fontkit;
        pdfDoc.registerFontkit(fontkitInstance);
      } catch (fontkitError) {
        console.error('❌ Failed to register fontkit:', fontkitError);
        // Continue without fontkit - will use standard fonts only
      }
      
      // Helper function to load Thai fonts with correct path priority
      const loadThaiFont = async (fontFileName) => {
        const possiblePaths = [
          `/fonts/${fontFileName}`,              // Primary: Working path from logs
          `./fonts/${fontFileName}`,             // Relative fonts
          `/public/fonts/${fontFileName}`,       // Public fonts folder  
          `./public/fonts/${fontFileName}`,      // Relative public/fonts
          `/public/${fontFileName}`,             // Public subfolder
          `/${fontFileName}`,                    // Root public folder
          `./public/${fontFileName}`,            // Relative public
          `/static/${fontFileName}`,             // Static folder
          `./${fontFileName}`,                   // Current directory
        ];
        
        for (const path of possiblePaths) {
          try {
            const response = await fetch(path);
            
            if (response.ok) {
              const fontBytes = await response.arrayBuffer();
              const embeddedFont = await pdfDoc.embedFont(fontBytes);
              return embeddedFont;
            }
          } catch (error) {
            continue;
          }
        }
        
        throw new Error(`Font ${fontFileName} not found in any of the attempted paths`);
      };

      // Load fonts with comprehensive fallback strategy
      let thaiFont, thaiFontBold;
      let thaiSupported = false; // Track if Thai fonts are actually loaded
      
      try {
        // Try to load Thai fonts
        thaiFont = await loadThaiFont('THSarabunNew.ttf');
        
        try {
          thaiFontBold = await loadThaiFont('THSarabunNew-Bold.ttf');
        } catch (boldError) {
          thaiFontBold = thaiFont;
        }
        
        thaiSupported = true; // Mark Thai as supported
      } catch (error) {
        console.error('❌ Failed to load Thai fonts, falling back to Helvetica:', error);
        thaiFont = await pdfDoc.embedFont(StandardFonts.Helvetica);
        thaiFontBold = await pdfDoc.embedFont(StandardFonts.HelveticaBold);
        thaiSupported = false; // Mark Thai as NOT supported
      }
      
      const helveticaFont = await pdfDoc.embedFont(StandardFonts.Helvetica);
      const helveticaBoldFont = await pdfDoc.embedFont(StandardFonts.HelveticaBold);

      // Page settings
      const pageWidth = 842; // A4 landscape width in points
      const pageHeight = 595; // A4 landscape height in points
      const margin = 40;
      const usableWidth = pageWidth - (2 * margin);
      
      const title = 'รายงานภาษีขาย';
      const prefixLabel = prefixOptions.find(p => p.value === filters.prefix)?.label || '';
      const monthLabel = monthOptions.find(m => m.value === filters.month)?.label || '';
      const subtitle = `${prefixLabel} | ${monthLabel} ${filters.year}`;

      // Table settings
      const rowsPerPage = 20;
      const rowsPerLastPage = 18; // Limit rows on last page to leave space for summary
      const rowHeight = 20;
      const headerHeight = 30; // Increased from 25 to 30 (20% increase) for multiline headers
      const headerY = pageHeight - 160;
      
      // Column widths (in points) - increased customer name column width
      const columnWidths = [50, 80, 80, 200, 110, 70, 80, 70, 80]; // Total: ~820 points
      
      // Column headers in Thai
      const headers = [
        'ลำดับ',
        'วันที่', 
        'เลขที่',
        'ชื่อผู้ซื้อสินค้า/ผู้รับบริการ',
        'เลขประจำตัวผู้เสียภาษี',
        'สถานประกอบการ',
        'มูลค่าสินค้า\nหรือบริการ',
        'จำนวนเงิน\nภาษีมูลค่าเพิ่ม',
        'รวมทั้งสิ้น'
      ];

      // Debug: Check customer data availability before PDF creation
      console.log('PDF Creation - Customer Cache size:', customerCache.size);
      console.log('PDF Creation - Customer Data keys:', Object.keys(customerData).length);
      
      // Use combined data (invoices + credit notes) for PDF - already sorted by combineInvoicesAndCreditNotes
      console.log('PDF - Combined data order (first 5):', combinedDataForPDF.slice(0, 5).map(item => `${item.displayDate} - ${item.displayNumber} (${item.type})`));
      
      // No need to clear customer data for cash invoices in PDF anymore
      // Let the display logic handle showing "เงินสด" when customer data is not available
      
      // Prepare table data
      const tableData = combinedDataForPDF.map((item, index) => {
        const beforeVat = calculateBeforeVat(item.TotalAmount, item.Vat_Amount);
        const custNo = item.Cust_No?.trim();
        const isCreditNote = item.type === 'credit_note';
        
        // Get customer data with fallback priority: cache -> state -> default
        let customer = { name: 'ไม่ระบุ', taxCode: '-', branchNo: 0 };
        let source = 'default';
        
        // Try to get customer data from cache or state
        if (custNo) {
          if (customerCache.has(custNo)) {
            customer = customerCache.get(custNo);
            source = 'cache';
          } else if (customerData[custNo]) {
            customer = customerData[custNo];
            source = 'state';
          }
        }
        
        // PRIORITY 1: หากไม่มีเลขบัตรประจำตัวผู้เสียภาษี (หรือเป็น '-') ให้แสดง เงินสด ทันที
        if (!customer || !customer.taxCode || customer.taxCode === '-' || customer.taxCode.trim() === '') {
          customer = { name: 'เงินสด', taxCode: '-', branchNo: 0 };
          source = 'no-tax-id';
          console.log(`🟡 PDF NO TAX ID: ${item.displayNumber} -> เงินสด (no valid taxCode)`);
        }
        // PRIORITY 2: หากมีเลขบัตรประจำตัวผู้เสียภาษี ให้เข้าเงื่อนไขการทำงานเหมือนเดิม
        else if (!isCreditNote) {
          const isCashInvoice = item.Type_Inv_Code === "1";
          const hasBillPrintType = item.Bill_Print_Type === true;
          
          // Force cash display if it's a cash invoice with Bill_Print_Type: true
          if (isCashInvoice && hasBillPrintType) {
            customer = { name: 'เงินสด', taxCode: '-', branchNo: 0 };
            source = 'cash-bill-print-type';
            console.log(`🟡 PDF CASH INVOICE with Bill_Print_Type: ${item.displayNumber} -> เงินสด (forced by Bill_Print_Type: true)`);
          }
          // If customer name is 'ไม่ระบุ' and it's cash invoice
          else if (customer.name === 'ไม่ระบุ' && isCashInvoice) {
            customer = { name: 'เงินสด', taxCode: '-', branchNo: 0 };
            source = 'cash-fallback';
          }
        }
        
        // Debug first few entries
        if (index < 5) {
          const isCashInvoice = item.Type_Inv_Code === "1";
          console.log(`Row ${index + 1}: Customer ${custNo} -> ${customer.name} (from ${source}) - Type: ${item.type} - Cash: ${isCashInvoice}`);
        }
        
        // Format document number based on type with CN type info
        let documentNumber;
        if (isCreditNote) {
          documentNumber = item.displayNumber;
        } else {
          documentNumber = formatInvoiceNumber(item);
        }
        
        // Apply customer name and tax ID overrides if user edited them
        const itemKey = item.displayNumber || item.Inv_Number;
        const finalCustomerName = customerNameOverrides[itemKey] || customer.name || 'ไม่ระบุ';
        const finalTaxCode = taxIdOverrides[itemKey] || customer.taxCode || '-';

        return [
          (index + 1).toString(),
          formatDate(item.displayDate),
          documentNumber,
          finalCustomerName,
          finalTaxCode,
          formatBranchNo(customer.branchNo || 0), // ใช้ BranchNo จริงจากข้อมูลลูกค้า
          formatNumber(beforeVat),
          formatNumber(item.Vat_Amount),
          formatNumber(item.TotalAmount)
        ];
      });

      // Calculate pages with special handling for last page
      let totalPdfPages;
      if (tableData.length === 0) {
        totalPdfPages = 1;
      } else {
        // Calculate if we need an extra page for summary
        const fullPages = Math.floor((tableData.length - 1) / rowsPerPage); // Full pages (except potential last)
        const remainingRows = tableData.length - (fullPages * rowsPerPage);
        
        if (remainingRows > rowsPerLastPage) {
          // Last page would have too many rows, need extra page for summary
          totalPdfPages = fullPages + 2;
        } else {
          // Last page can fit summary
          totalPdfPages = fullPages + 1;
        }
      }

      // Helper function to determine if text contains Thai characters
      const containsThai = (text) => {
        return /[\u0E00-\u0E7F]/.test(text);
      };

      // Helper function to use Thai font consistently for all text
      const selectFont = (text, requestedFont, thaiFont, thaiFontBold, helveticaFont, helveticaBoldFont) => {
        if (thaiSupported) {
          // Always use Thai font when available for consistent appearance
          return (requestedFont === thaiFontBold || requestedFont === helveticaBoldFont) ? thaiFontBold : thaiFont;
        } else {
          // Fallback to Helvetica only when Thai fonts are not available
          return (requestedFont === thaiFontBold || requestedFont === helveticaBoldFont) ? helveticaBoldFont : helveticaFont;
        }
      };

      // Helper function to prepare text for display
      const getDisplayText = (text) => {
        if (containsThai(text) && !thaiSupported) {
          return 'N/A'; // Replace Thai text with N/A when fonts not available
        }
        return text; // Keep all text as-is when Thai fonts are available
      };

      // Helper function to draw text in a cell with safe Thai handling
      const drawCell = (page, text, x, y, width, height, requestedFont, fontSize, color = rgb(0, 0, 0), align = 'left') => {
        // Get safe display text (converts Thai to N/A if not supported)
        const displayText = getDisplayText(text);
        
        // Check if text contains line breaks
        const lines = displayText.split('\n');
        
        // Select appropriate font for the display text
        const selectedFont = selectFont(displayText, requestedFont, thaiFont, thaiFontBold, helveticaFont, helveticaBoldFont);
        
        try {
          if (lines.length > 1) {
            // Multi-line text
            const lineHeight = fontSize + 2;
            const totalTextHeight = lines.length * lineHeight;
            let startY = y + (height + totalTextHeight) / 2 - lineHeight;
            
            lines.forEach((line, index) => {
              const trimmedLine = line.trim();
              if (trimmedLine) {
                const textWidth = selectedFont.widthOfTextAtSize(trimmedLine, fontSize);
                let textX = x + 5; // Default left padding
                
                if (align === 'center') {
                  textX = x + (width - textWidth) / 2;
                } else if (align === 'right') {
                  textX = x + width - textWidth - 5;
                }
                
                // Ensure text doesn't overflow
                let finalText = trimmedLine;
                if (textWidth > width - 10) {
                  const maxChars = Math.floor((width - 10) / (fontSize * 0.6));
                  finalText = trimmedLine.substring(0, maxChars) + '...';
                  textX = x + 5;
                }
                
                page.drawText(finalText, {
                  x: textX,
                  y: startY - (index * lineHeight),
                  size: fontSize,
                  font: selectedFont,
                  color: color,
                });
              }
            });
          } else {
            // Single line text (existing logic)
            const textWidth = selectedFont.widthOfTextAtSize(displayText, fontSize);
            let textX = x + 5; // Default left padding
            
            if (align === 'center') {
              textX = x + (width - textWidth) / 2;
            } else if (align === 'right') {
              textX = x + width - textWidth - 5;
            }
            
            // Ensure text doesn't overflow
            let finalText = displayText;
            if (textWidth > width - 10) {
              const maxChars = Math.floor((width - 10) / (fontSize * 0.6));
              finalText = displayText.substring(0, maxChars) + '...';
              textX = x + 5;
            }
            
            page.drawText(finalText, {
              x: textX,
              y: y + (height - fontSize) / 2,
              size: fontSize,
              font: selectedFont,
              color: color,
            });
          }
          
        } catch (error) {
          console.error('Error drawing text:', displayText, 'Font:', selectedFont?.name || 'unknown', error);
          
          // Last resort fallback
          try {
            page.drawText('N/A', {
              x: x + 5,
              y: y + (height - fontSize) / 2,
              size: fontSize,
              font: helveticaFont,
              color: color,
            });
          } catch (fallbackError) {
            console.error('Complete fallback failed:', fallbackError);
          }
        }
      };

      // Table border functions removed - clean table design

      // Create pages
      for (let pageNum = 0; pageNum < totalPdfPages; pageNum++) {
        const page = pdfDoc.addPage([pageWidth, pageHeight]);

        // === NEW HEADER LAYOUT ===
        
        // 1. Draw title (centered, top)
        const safeTitle = getDisplayText(title);
        const titleFont = selectFont(safeTitle, thaiFontBold, thaiFont, thaiFontBold, helveticaFont, helveticaBoldFont);
        
        try {
          page.drawText(safeTitle, {
            x: pageWidth / 2 - titleFont.widthOfTextAtSize(safeTitle, 16) / 2,
            y: pageHeight - 30,
            size: 16,
            font: titleFont,
            color: rgb(0, 0, 0),
          });
        } catch (error) {
          console.error('Error drawing title:', error);
          const fallbackTitle = 'Sales Report';
          page.drawText(fallbackTitle, {
            x: pageWidth / 2 - helveticaBoldFont.widthOfTextAtSize(fallbackTitle, 16) / 2,
            y: pageHeight - 30,
            size: 16,
            font: helveticaBoldFont,
            color: rgb(0, 0, 0),
          });
        }

        // 2. Draw company information (left side, 3 lines) with bold titles
        const companyInfo = [
          { title: 'ชื่อผู้ประกอบการ', value: ` ${process.env.REACT_APP_COMPANY_BRANCH_TH}` },
          { title: 'ชื่อสถานประกอบการ', value: ` ${process.env.REACT_APP_COMPANY_ADDRESS_TH}` },
          { title: 'เลขประจำตัวผู้เสียภาษี', value: ` ${process.env.REACT_APP_COMPANY_TAX_ID}` }
        ];
        
        companyInfo.forEach((item, index) => {
          const yPos = pageHeight - 55 - (index * 18);
          let currentX = margin;
          
          // Draw bold title
          const safeTitle = getDisplayText(item.title);
          const titleFont = selectFont(safeTitle, thaiFontBold, thaiFontBold, thaiFontBold, helveticaBoldFont, helveticaBoldFont);
          
          try {
            page.drawText(safeTitle, {
              x: currentX,
              y: yPos,
              size: 11,
              font: titleFont,
              color: rgb(0, 0, 0),
            });
            
            // Calculate width of title to position value text
            currentX += titleFont.widthOfTextAtSize(safeTitle, 11) + 3;
          } catch (error) {
            console.error(`Error drawing company title ${index + 1}:`, error);
            const fallbackTitles = ['Company:', 'Address:', 'Tax ID:'];
            page.drawText(fallbackTitles[index], {
              x: currentX,
              y: yPos,
              size: 11,
              font: helveticaBoldFont,
              color: rgb(0, 0, 0),
            });
            currentX += helveticaBoldFont.widthOfTextAtSize(fallbackTitles[index], 11) + 3;
          }
          
          // Draw regular value text
          const safeValue = getDisplayText(item.value);
          const valueFont = selectFont(safeValue, thaiFont, thaiFont, thaiFont, helveticaFont, helveticaFont);
          
          try {
            page.drawText(safeValue, {
              x: currentX,
              y: yPos,
              size: 11,
              font: valueFont,
              color: rgb(0, 0, 0),
            });
          } catch (error) {
            console.error(`Error drawing company value ${index + 1}:`, error);
            const fallbackValues = [' Thung Khok Branch', ' 140 Moo 6, Thung Khok, Song Phi Nong, Suphanburi 72190', ' 0105558116080'];
            page.drawText(fallbackValues[index], {
              x: currentX,
              y: yPos,
              size: 11,
              font: helveticaFont,
              color: rgb(0, 0, 0),
            });
          }
        });

        // 3. Draw page number (right side, aligned with first company line)
        const pageText = `หน้า ${pageNum + 1}/${totalPdfPages}`;
        const pageFont = selectFont(pageText, thaiFont, thaiFont, thaiFontBold, helveticaFont, helveticaBoldFont);
        
        try {
          page.drawText(pageText, {
            x: pageWidth - margin - pageFont.widthOfTextAtSize(pageText, 10),
            y: pageHeight - 55,
            size: 11,
            font: pageFont,
            color: rgb(0, 0, 0),
          });
        } catch (error) {
          console.error('Error drawing page number:', error);
          const fallbackPageText = `Page ${pageNum + 1}/${totalPdfPages}`;
          page.drawText(fallbackPageText, {
            x: pageWidth - margin - helveticaFont.widthOfTextAtSize(fallbackPageText, 10),
            y: pageHeight - 55,
            size: 11,
            font: helveticaFont,
            color: rgb(0, 0, 0),
          });
        }

        // 4. Draw printer info (right side, aligned with second company line)
        const storedUserData = localStorage.getItem('userData');
        const currentUser = storedUserData ? JSON.parse(storedUserData) : null;
        const userName = currentUser?.fullName || currentUser?.firstName || 'ไม่ระบุ';
        
        const printerText = `ผู้พิมพ์: ${userName}`;
        const safePrinterText = getDisplayText(printerText);
        const printerFont = selectFont(safePrinterText, thaiFont, thaiFont, thaiFontBold, helveticaFont, helveticaBoldFont);
        
        try {
          page.drawText(safePrinterText, {
            x: pageWidth - margin - printerFont.widthOfTextAtSize(safePrinterText, 9),
            y: pageHeight - 70,
            size: 10,
            font: printerFont,
            color: rgb(0, 0, 0),
          });
        } catch (error) {
          console.error('Error drawing printer info:', error);
          const fallbackPrinter = `Printer: ${userName}`;
          page.drawText(fallbackPrinter, {
            x: pageWidth - margin - helveticaFont.widthOfTextAtSize(fallbackPrinter, 9),
            y: pageHeight - 70,
            size: 10,
            font: helveticaFont,
            color: rgb(0, 0, 0),
          });
        }

        // 5. Draw month/year (centered, below everything)
        const monthLabel = monthOptions.find(m => m.value === filters.month)?.label || '';
        const monthYearTitle = `เดือน ${monthLabel} ${filters.year}`;
        const safeMonthYear = getDisplayText(monthYearTitle);
        const monthYearFont = selectFont(safeMonthYear, thaiFontBold, thaiFont, thaiFontBold, helveticaFont, helveticaBoldFont);
        
        try {
          page.drawText(safeMonthYear, {
            x: pageWidth / 2 - monthYearFont.widthOfTextAtSize(safeMonthYear, 14) / 2,
            y: pageHeight - 110,
            size: 14,
            font: monthYearFont,
            color: rgb(0, 0, 0),
          });
        } catch (error) {
          console.error('Error drawing month/year title:', error);
        }


        // Calculate table dimensions
        const tableWidth = columnWidths.reduce((sum, width) => sum + width, 0);
        const startX = (pageWidth - tableWidth) / 2;
        const tableHeight = headerHeight + (rowsPerPage * rowHeight);

        // Draw top and bottom borders of header row
        // Top border
        page.drawLine({
          start: { x: startX, y: headerY + headerHeight },
          end: { x: startX + tableWidth, y: headerY + headerHeight },
          thickness: 0.5,
          color: rgb(0, 0, 0),
        });
        
        // Bottom border (under header)
        page.drawLine({
          start: { x: startX, y: headerY },
          end: { x: startX + tableWidth, y: headerY },
          thickness: 0.5,
          color: rgb(0, 0, 0),
        });

        // Draw header background
        page.drawRectangle({
          x: startX,
          y: headerY,
          width: tableWidth,
          height: headerHeight,
          color: rgb(1, 1, 1), // White header background
        });

        // Row separators removed - clean table without borders

        // Draw headers with proper alignment
        let currentX = startX;
        for (let i = 0; i < headers.length; i++) {
          let headerAlign = 'center'; // Default center
          if (i === 3) headerAlign = 'left';  // ชื่อผู้ซื้อสินค้า - left align
          if (i >= 5) headerAlign = 'right'; // มูลค่าสินค้า, ภาษี, รวม - right align
          
          drawCell(
            page,
            headers[i],
            currentX,
            headerY,
            columnWidths[i],
            headerHeight,
            thaiFontBold,
            11,
            rgb(0, 0, 0), // Black text
            headerAlign
          );
          currentX += columnWidths[i];
        }

        // Draw data rows with special handling for last page
        const isLastPage = pageNum === totalPdfPages - 1;
        let startIdx = pageNum * rowsPerPage;
        const isSummaryOnlyPage = isLastPage && startIdx >= tableData.length;
        let endIdx, pageData;
        
        if (isSummaryOnlyPage) {
          // This is a summary-only page
          pageData = [];
        } else if (isLastPage) {
          // Last page with data - limit to rowsPerLastPage if not first page
          const remainingRows = tableData.length - startIdx;
          if (remainingRows > rowsPerLastPage && totalPdfPages > 1) {
            // Too many rows for last page, move some to next page
            endIdx = startIdx + rowsPerLastPage;
          } else {
            endIdx = tableData.length;
          }
          pageData = tableData.slice(startIdx, endIdx);
        } else {
          // Regular page
          endIdx = Math.min(startIdx + rowsPerPage, tableData.length);
          pageData = tableData.slice(startIdx, endIdx);
        }

        for (let rowIdx = 0; rowIdx < pageData.length; rowIdx++) {
          const row = pageData[rowIdx];
          const y = headerY - ((rowIdx + 1) * rowHeight);
          
          currentX = startX;
          for (let colIdx = 0; colIdx < row.length; colIdx++) {
            let align = 'left'; // Default left
            if (colIdx === 0 || colIdx === 1 || colIdx === 2 || colIdx === 4) align = 'center'; // ลำดับ, วันที่, เลขที่, เลขภาษี
            if (colIdx === 3) align = 'left'; // ชื่อผู้ซื้อสินค้า - left align
            if (colIdx >= 5) align = 'right'; // มูลค่าสินค้า, ภาษี, รวม - right align
            
            drawCell(
              page,
              row[colIdx] || '',
              currentX,
              y,
              columnWidths[colIdx],
              rowHeight,
              thaiFont,
              10,
              rgb(0, 0, 0),
              align
            );
            currentX += columnWidths[colIdx];
          }
        }

        // Add bottom line for last data row on the last page
        if (pageNum === totalPdfPages - 1 && pageData.length > 0) {
          // Draw line under the last data row
          const lastDataRowY = headerY - (pageData.length * rowHeight);
          page.drawLine({
            start: { x: startX, y: lastDataRowY },
            end: { x: startX + tableWidth, y: lastDataRowY },
            thickness: 0.5,
            color: rgb(0, 0, 0),
          });
        }

        // Add summary totals to the last page only
        if (pageNum === totalPdfPages - 1) {
          // Calculate totals from all data
          let totalBeforeVat = 0;
          let totalVat = 0;
          let grandTotal = 0;

          tableData.forEach(row => {
            // row[6] = มูลค่าสินค้า/บริการ (before VAT) - เปลี่ยนจาก row[5]
            // row[7] = จำนวนเงินภาษี (VAT) - เปลี่ยนจาก row[6]
            // row[8] = รวมทั้งสิ้น (total) - เปลี่ยนจาก row[7]
            const beforeVat = parseFloat(row[6]?.toString().replace(/,/g, '') || 0);
            const vat = parseFloat(row[7]?.toString().replace(/,/g, '') || 0);
            const total = parseFloat(row[8]?.toString().replace(/,/g, '') || 0);
            
            totalBeforeVat += beforeVat;
            totalVat += vat;
            grandTotal += total;
          });

          // Format numbers with commas
          const formatNumber = (num) => num.toLocaleString('th-TH', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

          // Draw summary line below table with proper alignment
          // Position summary based on whether this is summary-only page or page with data
          let summaryY;
          if (isSummaryOnlyPage) {
            // Summary-only page - start from near the top
            summaryY = headerY - (2 * rowHeight);
          } else {
            // Page with data - position below the data
            summaryY = headerY - ((pageData.length + 2) * rowHeight);
          }
          
          // Draw summary label
          const summaryLabel = 'รวมเงินทั้งสิ้น';
          const safeSummaryLabel = getDisplayText(summaryLabel);
          const summaryFont = selectFont(safeSummaryLabel, thaiFontBold, thaiFont, thaiFontBold, helveticaFont, helveticaBoldFont);
          
          try {
            // Position label in the customer name column area (right-aligned within that column)
            const labelColumnEnd = startX + columnWidths[0] + columnWidths[1] + columnWidths[2] + columnWidths[3] + columnWidths[4];
            page.drawText(safeSummaryLabel, {
              x: labelColumnEnd - summaryFont.widthOfTextAtSize(safeSummaryLabel, 11) - 10,
              y: summaryY,
              size: 11,
              font: summaryFont,
              color: rgb(0, 0, 0),
            });

            // Draw totals aligned with their respective columns
            let summaryX = startX;
            
            // Skip to column 6 (มูลค่าสินค้า/บริการ) - เปลี่ยนจาก column 5
            for (let i = 0; i < 6; i++) {
              summaryX += columnWidths[i];
            }
            
            // Column 6: มูลค่าสินค้า/บริการ (เปลี่ยนจาก Column 5)
            const beforeVatText = formatNumber(totalBeforeVat);
            const safeBeforeVatText = getDisplayText(beforeVatText);
            page.drawText(safeBeforeVatText, {
              x: summaryX + columnWidths[6] - summaryFont.widthOfTextAtSize(safeBeforeVatText, 11) - 5,
              y: summaryY,
              size: 11,
              font: summaryFont,
              color: rgb(0, 0, 0),
            });
            summaryX += columnWidths[6];
            
            // Column 7: จำนวนเงินภาษี (เปลี่ยนจาก Column 6)
            const vatText = formatNumber(totalVat);
            const safeVatText = getDisplayText(vatText);
            page.drawText(safeVatText, {
              x: summaryX + columnWidths[7] - summaryFont.widthOfTextAtSize(safeVatText, 11) - 5,
              y: summaryY,
              size: 11,
              font: summaryFont,
              color: rgb(0, 0, 0),
            });
            summaryX += columnWidths[7];
            
            // Column 8: รวมทั้งสิ้น (เปลี่ยนจาก Column 7)
            const totalText = formatNumber(grandTotal);
            const safeTotalText = getDisplayText(totalText);
            page.drawText(safeTotalText, {
              x: summaryX + columnWidths[8] - summaryFont.widthOfTextAtSize(safeTotalText, 11) - 5,
              y: summaryY,
              size: 11,
              font: summaryFont,
              color: rgb(0, 0, 0),
            });
            
          } catch (error) {
            console.error('Error drawing summary totals:', error);
          }

          // Add note about asterisk (*) symbol for cancelled invoices
          const noteY = summaryY - 50;
          const noteText = 'หมายเหตุ : เลขที่มีเครื่องหมาย * นำหน้า คือ เอกสารที่ถูกยกเลิก';
          const safeNoteText = getDisplayText(noteText);
          const noteFont = selectFont(safeNoteText, thaiFont, thaiFont, thaiFont, helveticaFont, helveticaFont);
          
          try {
            page.drawText(safeNoteText, {
              x: startX,
              y: noteY,
              size: 9,
              font: noteFont,
              color: rgb(0.5, 0.5, 0.5), // Gray color for note
            });
          } catch (error) {
            console.error('Error drawing note:', error);
            // Fallback to English
            page.drawText('Note: Invoice numbers with * prefix are cancelled documents', {
              x: startX,
              y: noteY,
              size: 9,
              font: helveticaFont,
              color: rgb(0.5, 0.5, 0.5),
            });
          }
        }
      }

      // Update progress - generating PDF
      setPdfProgress(prev => ({
        ...prev,
        step: 'กำลังสร้างไฟล์ PDF...',
        percentage: 90
      }));

      // Generate PDF and download
      const pdfBytes = await pdfDoc.save();
      const fileName = `Invoice_Report_${filters.month}_${filters.year}_${filters.prefix}.pdf`;
      
      // Create download link
      const blob = new Blob([pdfBytes], { type: 'application/pdf' });
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = fileName;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);

      // Complete
      setPdfProgress(prev => ({
        ...prev,
        step: '✓ สร้าง PDF สำเร็จ',
        percentage: 100
      }));
      
      // Reset states
      setTimeout(() => {
        setPdfProgress({ show: false, step: '', current: 0, total: 0, percentage: 0 });
        setPdfLoading(false);
      }, 1500);
      
    } catch (error) {
      console.error('Error exporting PDF:', error);
      
      // Enhanced error handling with user-friendly messages
      let errorMessage = 'เกิดข้อผิดพลาดในการสร้าง PDF';
      
      if (error.message.includes('ไม่สามารถดึงข้อมูล')) {
        errorMessage += ': ไม่พบข้อมูลใบแจ้งหนี้';
      } else if (error.message.includes('โหลดข้อมูลลูกค้า')) {
        errorMessage += ': ปัญหาในการโหลดข้อมูลลูกค้า';
      } else {
        errorMessage += `: ${error.message}`;
      }
      
      // Show error in progress popup
      setPdfProgress(prev => ({
        ...prev,
        step: `❌ เกิดข้อผิดพลาด: ${error.message}`,
        percentage: 0
      }));

      setTimeout(() => {
        setPdfProgress({ show: false, step: '', current: 0, total: 0, percentage: 0 });
        setPdfLoading(false);
      }, 3000);
    }
  };

  const fetchCustomerData = async (custNo) => {
    if (customerData[custNo] || !custNo) return customerData[custNo];

    try {
      const response = await fetch('https://apisaleeasy.psisat.com/api/customererp/by-custno', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          custNo: custNo.trim()
        })
      });

      const result = await response.json();
      
      if (result.status && result.data) {
        const newCustomerData = {
          ...customerData,
          [custNo]: {
            name: result.data.Cust_Name || 'ไม่ระบุ',
            taxCode: result.data.TaxCode || '-',
            branchNo: result.data.BranchNo || 0
          }
        };
        setCustomerData(newCustomerData);
        return newCustomerData[custNo];
      } else {
        const defaultData = { name: 'ไม่ระบุ', taxCode: '-', branchNo: 0 };
        setCustomerData(prev => ({ ...prev, [custNo]: defaultData }));
        return defaultData;
      }
    } catch (error) {
      console.error('Error fetching customer:', error);
      const defaultData = { name: 'ไม่ระบุ', taxCode: '-', branchNo: 0 };
      setCustomerData(prev => ({ ...prev, [custNo]: defaultData }));
      return defaultData;
    }
  };

  // Enhanced API configuration
  const API_CONFIG = {
    BASE_TIMEOUT: 10000, // 10 seconds
    MAX_RETRIES: 5,
    INITIAL_DELAY: 1000, // 1 second
    MAX_DELAY: 30000, // 30 seconds
    BATCH_SIZE_MIN: 3,
    BATCH_SIZE_MAX: 10,
    BATCH_SIZE_DEFAULT: 5,
    REQUEST_INTERVAL: 200, // ms between batches
    SUCCESS_RATE_THRESHOLD: 0.8, // 80% success rate
    PERFORMANCE_WINDOW: 10 // Track last 10 requests
  };

  // Performance tracking for dynamic optimization
  const [performanceMetrics, setPerformanceMetrics] = useState({
    successRate: 1.0,
    avgResponseTime: 0,
    recentRequests: [],
    optimalBatchSize: API_CONFIG.BATCH_SIZE_DEFAULT
  });

  // Request queue for throttling
  const [requestQueue] = useState([]);
  const [isProcessingQueue, setIsProcessingQueue] = useState(false);
  
  // Circuit breaker pattern for API resilience
  const [circuitBreaker] = useState(() => {
    const FAILURE_THRESHOLD = 5;
    const RECOVERY_TIMEOUT = 30000; // 30 seconds
    const SUCCESS_THRESHOLD = 3;
    
    return {
      state: 'CLOSED', // CLOSED, OPEN, HALF_OPEN
      failureCount: 0,
      successCount: 0,
      lastFailureTime: 0,
      
      canExecute() {
        switch (this.state) {
          case 'CLOSED':
            return true;
          case 'OPEN':
            if (Date.now() - this.lastFailureTime >= RECOVERY_TIMEOUT) {
              this.state = 'HALF_OPEN';
              this.successCount = 0;
              console.log('Circuit breaker moving to HALF_OPEN state');
              return true;
            }
            return false;
          case 'HALF_OPEN':
            return true;
          default:
            return false;
        }
      },
      
      onSuccess() {
        switch (this.state) {
          case 'HALF_OPEN':
            this.successCount++;
            if (this.successCount >= SUCCESS_THRESHOLD) {
              this.state = 'CLOSED';
              this.failureCount = 0;
              console.log('Circuit breaker CLOSED - service recovered');
            }
            break;
          case 'CLOSED':
            this.failureCount = Math.max(0, this.failureCount - 1);
            break;
        }
      },
      
      onFailure() {
        this.failureCount++;
        this.lastFailureTime = Date.now();
        
        if (this.state === 'HALF_OPEN') {
          this.state = 'OPEN';
          console.log('Circuit breaker OPEN - service still failing');
        } else if (this.state === 'CLOSED' && this.failureCount >= FAILURE_THRESHOLD) {
          this.state = 'OPEN';
          console.log('Circuit breaker OPEN - too many failures');
        }
      },
      
      getState() {
        return {
          state: this.state,
          failureCount: this.failureCount,
          successCount: this.successCount,
          canExecute: this.canExecute()
        };
      }
    };
  });

  // Enhanced timeout handler with AbortController
  const createTimeoutController = (timeoutMs = API_CONFIG.BASE_TIMEOUT) => {
    const controller = new AbortController();
    const timeoutId = setTimeout(() => {
      controller.abort();
    }, timeoutMs);
    
    return {
      controller,
      cleanup: () => clearTimeout(timeoutId)
    };
  };

  // Enhanced error classification
  const classifyError = (error, response = null) => {
    if (error.name === 'AbortError') {
      return { type: 'timeout', retryable: true, priority: 'high' };
    }
    if (error.name === 'TypeError' && error.message.includes('fetch')) {
      return { type: 'network', retryable: true, priority: 'high' };
    }
    if (response) {
      if (response.status === 429) {
        return { type: 'rate_limit', retryable: true, priority: 'medium' };
      }
      if (response.status >= 500) {
        return { type: 'server_error', retryable: true, priority: 'medium' };
      }
      if (response.status === 404) {
        return { type: 'not_found', retryable: false, priority: 'low' };
      }
      if (response.status >= 400) {
        return { type: 'client_error', retryable: false, priority: 'low' };
      }
    }
    return { type: 'unknown', retryable: true, priority: 'low' };
  };

  // Advanced retry mechanism with exponential backoff and jitter
  const calculateRetryDelay = (attempt, baseDelay = API_CONFIG.INITIAL_DELAY, maxDelay = API_CONFIG.MAX_DELAY) => {
    const exponentialDelay = baseDelay * Math.pow(2, attempt - 1);
    const jitter = Math.random() * 0.3 * exponentialDelay; // 30% jitter
    return Math.min(exponentialDelay + jitter, maxDelay);
  };

  // Performance tracking update
  const updatePerformanceMetrics = (requestData) => {
    setPerformanceMetrics(prev => {
      const newRequests = [...prev.recentRequests, requestData].slice(-API_CONFIG.PERFORMANCE_WINDOW);
      const successCount = newRequests.filter(req => req.success).length;
      const successRate = successCount / newRequests.length;
      const avgResponseTime = newRequests.reduce((sum, req) => sum + req.responseTime, 0) / newRequests.length;
      
      // Dynamic batch size optimization
      let optimalBatchSize = prev.optimalBatchSize;
      if (successRate < API_CONFIG.SUCCESS_RATE_THRESHOLD || avgResponseTime > 5000) {
        optimalBatchSize = Math.max(API_CONFIG.BATCH_SIZE_MIN, optimalBatchSize - 1);
      } else if (successRate > 0.95 && avgResponseTime < 2000) {
        optimalBatchSize = Math.min(API_CONFIG.BATCH_SIZE_MAX, optimalBatchSize + 1);
      }
      
      return {
        successRate,
        avgResponseTime,
        recentRequests: newRequests,
        optimalBatchSize
      };
    });
  };

  // Enhanced customer data fetching with comprehensive error handling
  const fetchCustomerWithRetry = async (custNo, options = {}) => {
    // Check circuit breaker first
    if (!circuitBreaker.canExecute()) {
      console.warn(`Circuit breaker OPEN - skipping request for customer ${custNo}`);
      return {
        custNo,
        success: false,
        error: 'Circuit breaker OPEN - API temporarily unavailable',
        data: { name: 'บริการไม่พร้อมใช้งานชั่วคราว', taxCode: '-' },
        attempts: 0,
        circuitBreakerOpen: true
      };
    }
    
    const {
      maxRetries = API_CONFIG.MAX_RETRIES,
      baseDelay = API_CONFIG.INITIAL_DELAY,
      timeout = API_CONFIG.BASE_TIMEOUT,
      priority = 'normal'
    } = options;

    let lastError;
    let response;
    const startTime = Date.now();

    for (let attempt = 1; attempt <= maxRetries; attempt++) {
      const { controller, cleanup } = createTimeoutController(timeout + (attempt * 2000));
      const requestStart = Date.now(); // Move outside try block
      
      try {
        
        response = await fetch(`${process.env.REACT_APP_API_BASE_URL}${process.env.REACT_APP_CUSTOMER_ENDPOINT}`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'X-Request-ID': `${custNo}-${Date.now()}-${attempt}`,
            'X-Retry-Attempt': attempt.toString()
          },
          body: JSON.stringify({
            custNo: custNo.trim()
          }),
          signal: controller.signal
        });
        
        const responseTime = Date.now() - requestStart;
        cleanup();

        // Check if response is ok
        if (!response.ok) {
          const errorClassification = classifyError(null, response);
          
          if (!errorClassification.retryable) {
            updatePerformanceMetrics({
              success: false,
              responseTime,
              error: `HTTP ${response.status}`,
              attempt,
              custNo
            });
            
            return {
              custNo,
              success: false,
              error: `HTTP ${response.status}`,
              data: { name: 'ข้อมูลไม่พร้อมใช้งาน', taxCode: '-' },
              attempts: attempt
            };
          }
          
          throw new Error(`HTTP ${response.status}: ${errorClassification.type}`);
        }

        const result = await response.json();
        
        updatePerformanceMetrics({
          success: true,
          responseTime,
          attempt,
          custNo
        });
        
        // Update API health monitoring
        updateApiHealth(responseTime, true);
        
        if (result.status && result.data) {
          // Success - update circuit breaker
          circuitBreaker.onSuccess();
          
          return {
            custNo,
            success: true,
            data: {
              name: result.data.Cust_Name?.trim() || 'ไม่ระบุ',
              taxCode: result.data.TaxCode?.trim() || '-'
            },
            attempts: attempt,
            responseTime
          };
        } else {
          // Partial success - still update circuit breaker
          circuitBreaker.onSuccess();
          
          return {
            custNo,
            success: true,
            data: { name: 'ไม่พบข้อมูล', taxCode: '-' },
            attempts: attempt,
            responseTime
          };
        }
        
      } catch (error) {
        cleanup();
        lastError = error;
        const errorClassification = classifyError(error, response);
        const responseTime = Date.now() - requestStart;
        
        console.warn(`Attempt ${attempt}/${maxRetries} failed for customer ${custNo}:`, {
          error: error.message,
          type: errorClassification.type,
          retryable: errorClassification.retryable
        });
        
        updatePerformanceMetrics({
          success: false,
          responseTime,
          error: error.message,
          attempt,
          custNo
        });
        
        // Update API health monitoring
        updateApiHealth(responseTime, false, errorClassification.type);
        
        if (attempt === maxRetries || !errorClassification.retryable) {
          // Final failure - update circuit breaker
          circuitBreaker.onFailure();
          
          console.error(`Final attempt failed for customer ${custNo}:`, error);
          return {
            custNo,
            success: false,
            error: error.message,
            data: { name: 'เกิดข้อผิดพลาด', taxCode: '-' },
            attempts: attempt,
            errorType: errorClassification.type
          };
        } else if (attempt < maxRetries) {
          // Intermediate failure - update circuit breaker
          circuitBreaker.onFailure();
        }
        
        // Calculate retry delay based on error type and attempt
        let retryDelay = calculateRetryDelay(attempt, baseDelay);
        
        // Increase delay for rate limiting
        if (errorClassification.type === 'rate_limit') {
          retryDelay *= 3;
        }
        
        // Adjust delay based on priority
        if (priority === 'high') {
          retryDelay *= 0.7;
        } else if (priority === 'low') {
          retryDelay *= 1.5;
        }
        
        await new Promise(resolve => setTimeout(resolve, retryDelay));
      }
    }

    // This should never be reached, but just in case
    return {
      custNo,
      success: false,
      error: lastError?.message || 'Unknown error',
      data: { name: 'เกิดข้อผิดพลาด', taxCode: '-' },
      attempts: maxRetries
    };
  };

  // Request queue processing for throttling
  const processRequestQueue = async () => {
    if (isProcessingQueue || requestQueue.length === 0) {
      return;
    }

    setIsProcessingQueue(true);
    
    try {
      while (requestQueue.length > 0) {
        const request = requestQueue.shift();
        const result = await fetchCustomerWithRetry(request.custNo, request.options);
        request.resolve(result);
        
        // Throttle requests
        if (requestQueue.length > 0) {
          await new Promise(resolve => setTimeout(resolve, API_CONFIG.REQUEST_INTERVAL));
        }
      }
    } catch (error) {
      console.error('Error processing request queue:', error);
    } finally {
      setIsProcessingQueue(false);
    }
  };

  // Queued customer fetch for throttling
  const fetchCustomerQueued = async (custNo, options = {}) => {
    return new Promise((resolve, reject) => {
      requestQueue.push({
        custNo,
        options,
        resolve,
        reject,
        timestamp: Date.now()
      });
      
      // Start processing if not already running
      processRequestQueue().catch(reject);
    });
  };

  // Enhanced data validation
  const validateCustomerData = (data, custNo) => {
    const errors = [];
    
    if (!data) {
      errors.push('Data is null or undefined');
      return { isValid: false, errors };
    }
    
    if (!data.name || typeof data.name !== 'string') {
      errors.push('Invalid or missing customer name');
    }
    
    if (data.name && data.name.length > 200) {
      errors.push('Customer name too long');
    }
    
    if (data.taxCode && typeof data.taxCode !== 'string') {
      errors.push('Invalid tax code format');
    }
    
    return {
      isValid: errors.length === 0,
      errors,
      sanitizedData: {
        name: (data.name || '').trim().substring(0, 200) || 'ไม่ระบุ',
        taxCode: (data.taxCode || '').trim() || '-',
        branchNo: data.branchNo || 0
      }
    };
  };

  // Memory-efficient batch processing for large datasets
  const createOptimizedBatches = (custNosToFetch, optimalBatchSize) => {
    const batches = [];
    const totalMemoryMB = (custNosToFetch.length * 1024) / (1024 * 1024); // Rough estimation
    
    // Reduce batch size for very large datasets to prevent memory issues
    let adjustedBatchSize = optimalBatchSize;
    if (totalMemoryMB > 100) { // > 100MB estimated
      adjustedBatchSize = Math.max(2, Math.floor(optimalBatchSize / 2));
      console.log(`Large dataset detected (${custNosToFetch.length} items), reducing batch size to ${adjustedBatchSize}`);
    }
    
    for (let i = 0; i < custNosToFetch.length; i += adjustedBatchSize) {
      batches.push(custNosToFetch.slice(i, i + adjustedBatchSize));
    }
    
    return { batches, adjustedBatchSize };
  };

  // Remove this complex function - replaced with simple version below
  /*
  const fetchAllCustomerDataEnhanced = async (invoiceList, onProgress = null) => {
    const startTime = Date.now();
    const uniqueCustNos = [...new Set(invoiceList.map(inv => inv.Cust_No?.trim()).filter(Boolean))];
    
    // Advanced caching: Check both memory cache and state cache
    const custNosToFetch = uniqueCustNos.filter(custNo => {
      // Check memory cache first (with TTL)
      const memCached = customerCache.get(custNo);
      if (memCached) {
        const validation = validateCustomerData(memCached, custNo);
        if (validation.isValid) {
          // Update state cache if not present
          if (!customerData[custNo]) {
            setCustomerData(prev => ({ ...prev, [custNo]: validation.sanitizedData }));
          }
          return false;
        }
      }
      
      // Check state cache
      const stateCached = customerData[custNo];
      if (stateCached) {
        const validation = validateCustomerData(stateCached, custNo);
        if (validation.isValid) {
          // Update memory cache
          customerCache.set(custNo, validation.sanitizedData);
          return false;
        }
      }
      
      return true;
    });
    
    if (custNosToFetch.length === 0) {
      if (onProgress) {
        onProgress({ 
          current: 0, 
          total: 0, 
          completed: true,
          message: 'ข้อมูลลูกค้าพร้อมใช้งาน (จากแคช)'
        });
      }
      return { success: true, loadedCount: 0, failedCount: 0, cached: uniqueCustNos.length };
    }

    console.log(`Loading customer data: ${custNosToFetch.length} new, ${uniqueCustNos.length - custNosToFetch.length} cached`);

    const { batches, adjustedBatchSize } = createOptimizedBatches(custNosToFetch, performanceMetrics.optimalBatchSize);
    
    let totalProcessed = 0;
    let successCount = 0;
    let failedCount = 0;
    const newCustomerData = { ...customerData };
    const failedCustomers = [];
    const processingStats = {
      totalRequests: 0,
      totalRetries: 0,
      avgResponseTime: 0,
      errorsByType: {}
    };

    try {
      for (let batchIndex = 0; batchIndex < batches.length; batchIndex++) {
        const batch = batches[batchIndex];
        const batchStartTime = Date.now();
        
        if (onProgress) {
          onProgress({
            current: totalProcessed,
            total: custNosToFetch.length,
            message: `กำลังโหลดข้อมูลลูกค้า... Batch ${batchIndex + 1}/${batches.length} (${totalProcessed}/${custNosToFetch.length})`,
            completed: false,
            batchInfo: {
              currentBatch: batchIndex + 1,
              totalBatches: batches.length,
              batchSize: adjustedBatchSize,
              successRate: totalProcessed > 0 ? (successCount / totalProcessed) : 1
            }
          });
        }
        
        // Update customer loading progress state for UI
        setCustomerLoadingProgress({ 
          current: totalProcessed, 
          total: custNosToFetch.length,
          batchInfo: `Batch ${batchIndex + 1}/${batches.length}`
        });

        // Process batch with different strategies based on performance
        let batchResults;
        const currentSuccessRate = totalProcessed > 0 ? (successCount / totalProcessed) : 1;
        
        if (currentSuccessRate < API_CONFIG.SUCCESS_RATE_THRESHOLD) {
          // Low success rate: Process sequentially with higher priority
          console.log('Low success rate detected, switching to sequential processing');
          batchResults = [];
          for (const custNo of batch) {
            const result = await fetchCustomerWithRetry(custNo, { priority: 'high' });
            batchResults.push({ status: 'fulfilled', value: result });
            
            // Small delay between sequential requests
            if (batch.indexOf(custNo) < batch.length - 1) {
              await new Promise(resolve => setTimeout(resolve, API_CONFIG.REQUEST_INTERVAL * 2));
            }
          }
        } else {
          // Normal success rate: Process in parallel
          const batchPromises = batch.map(custNo => fetchCustomerWithRetry(custNo, { priority: 'normal' }));
          batchResults = await Promise.allSettled(batchPromises);
        }
        
        // Process and validate results
        batchResults.forEach((result, index) => {
          totalProcessed++;
          processingStats.totalRequests++;
          
          if (result.status === 'fulfilled' && result.value) {
            const customerResult = result.value;
            processingStats.totalRetries += (customerResult.attempts || 1) - 1;
            processingStats.avgResponseTime += customerResult.responseTime || 0;
            
            // Validate and sanitize data
            const validation = validateCustomerData(customerResult.data, customerResult.custNo);
            
            if (validation.isValid) {
              newCustomerData[customerResult.custNo] = validation.sanitizedData;
              // Update memory cache
              customerCache.set(customerResult.custNo, validation.sanitizedData);
              if (customerResult.success) {
                successCount++;
              }
            } else {
              console.warn(`Invalid customer data for ${customerResult.custNo}:`, validation.errors);
              newCustomerData[customerResult.custNo] = validation.sanitizedData; // Use sanitized fallback
              // Cache fallback data too
              customerCache.set(customerResult.custNo, validation.sanitizedData);
            }
            
            if (!customerResult.success) {
              failedCount++;
              const errorType = customerResult.errorType || 'unknown';
              processingStats.errorsByType[errorType] = (processingStats.errorsByType[errorType] || 0) + 1;
              
              failedCustomers.push({
                custNo: customerResult.custNo,
                error: customerResult.error || 'Unknown error',
                attempts: customerResult.attempts || 1,
                errorType
              });
            }
          } else {
            failedCount++;
            const custNo = batch[index];
            const errorMsg = result.reason?.message || 'Promise rejected';
            processingStats.errorsByType['promise_rejected'] = (processingStats.errorsByType['promise_rejected'] || 0) + 1;
            
            failedCustomers.push({
              custNo: custNo,
              error: errorMsg,
              attempts: 1,
              errorType: 'promise_rejected'
            });
            
            // Provide fallback data
            const fallbackData = { name: 'เกิดข้อผิดพลาด', taxCode: '-', branchNo: 0 };
            newCustomerData[custNo] = fallbackData;
            // Cache fallback data to prevent repeated failures
            customerCache.set(custNo, fallbackData);
          }
          
          if (onProgress) {
            const currentSuccessRate = (successCount / totalProcessed) * 100;
            onProgress({
              current: totalProcessed,
              total: custNosToFetch.length,
              message: `กำลังโหลดข้อมูลลูกค้า... ${totalProcessed}/${custNosToFetch.length} (${currentSuccessRate.toFixed(1)}% สำเร็จ)`,
              completed: false,
              stats: {
                successRate: currentSuccessRate,
                avgResponseTime: processingStats.avgResponseTime / processingStats.totalRequests
              }
            });
          }
          
          // Update customer loading progress state for UI
          setCustomerLoadingProgress({ 
            current: totalProcessed, 
            total: custNosToFetch.length,
            successRate: (successCount / totalProcessed) * 100
          });
        });
        
        const batchTime = Date.now() - batchStartTime;
        console.log(`Batch ${batchIndex + 1} completed in ${batchTime}ms, success rate: ${((batch.length - failedCustomers.slice(-batch.length).length) / batch.length * 100).toFixed(1)}%`);
        
        // Adaptive delay between batches based on performance
        if (batchIndex < batches.length - 1) {
          let delay = API_CONFIG.REQUEST_INTERVAL;
          
          // Increase delay if high error rate
          if (currentSuccessRate < API_CONFIG.SUCCESS_RATE_THRESHOLD) {
            delay *= 3;
          }
          
          // Increase delay if slow responses
          const avgResponseTime = processingStats.avgResponseTime / processingStats.totalRequests;
          if (avgResponseTime > 5000) {
            delay *= 2;
          }
          
          await new Promise(resolve => setTimeout(resolve, delay));
        }
      }
      
      // Update state with all results atomically
      setCustomerData(newCustomerData);
      
      const totalTime = Date.now() - startTime;
      const finalSuccessRate = (successCount / totalProcessed) * 100;
      const avgResponseTime = processingStats.avgResponseTime / processingStats.totalRequests;
      
      const loadingStats = {
        totalProcessed,
        successCount,
        failedCount,
        successRate: finalSuccessRate,
        totalTime,
        avgResponseTime,
        totalRetries: processingStats.totalRetries,
        errorsByType: processingStats.errorsByType,
        cacheStats: customerCache.getStats(),
        circuitBreakerState: circuitBreaker.getState(),
        memoryUsage: memoryUsage
      };
      
      console.log('Customer data loading completed:', loadingStats);
      
      if (debugMode) {
        console.group('Detailed Loading Statistics');
        console.table(failedCustomers.slice(0, 10)); // Show first 10 failures
        console.log('Circuit Breaker State:', circuitBreaker.getState());
        console.log('Cache Statistics:', customerCache.getStats());
        console.log('API Health:', apiHealth);
        console.groupEnd();
      }
      
      if (onProgress) {
        onProgress({
          current: totalProcessed,
          total: custNosToFetch.length,
          message: `โหลดข้อมูลลูกค้าเสร็จสิ้น ${successCount}/${custNosToFetch.length} สำเร็จ (${finalSuccessRate.toFixed(1)}%) ใน ${(totalTime/1000).toFixed(1)}s`,
          completed: true,
          stats: {
            successRate: finalSuccessRate,
            avgResponseTime,
            totalTime,
            retryCount: processingStats.totalRetries
          }
        });
      }
      
      // Reset customer loading progress
      setCustomerLoadingProgress({ current: 0, total: 0 });
      
      if (failedCustomers.length > 0) {
        console.warn(`Failed to load ${failedCustomers.length} customers:`, {
          count: failedCustomers.length,
          errorsByType: processingStats.errorsByType,
          samples: failedCustomers.slice(0, 5) // Show first 5 as examples
        });
      }
      
      return {
        success: true,
        loadedCount: successCount,
        failedCount: failedCount,
        failedCustomers: failedCustomers,
        stats: {
          totalTime,
          avgResponseTime,
          successRate: finalSuccessRate,
          retryCount: processingStats.totalRetries,
          errorsByType: processingStats.errorsByType
        }
      };
      
    } catch (error) {
      console.error('Critical error in fetchAllCustomerDataEnhanced:', error);
      
      if (onProgress) {
        onProgress({
          current: totalProcessed,
          total: custNosToFetch.length,
          message: `เกิดข้อผิดพลาดร้ายแรงในการโหลดข้อมูลลูกค้า: ${error.message}`,
          completed: true,
          error: true
        });
      }
      
      return {
        success: false,
        loadedCount: successCount,
        failedCount: custNosToFetch.length - successCount,
        error: error.message,
        stats: {
          totalTime: Date.now() - startTime,
          errorsByType: processingStats.errorsByType
        }
      };
    }
  };
  */

  const fetchAllCustomerData = async (invoiceList) => {
    const uniqueCustNos = [...new Set(invoiceList.map(inv => inv.Cust_No?.trim()).filter(Boolean))];
    
    // Filter out customer numbers that are already loaded
    const custNosToFetch = uniqueCustNos.filter(custNo => !customerData[custNo]);
    
    if (custNosToFetch.length === 0) {
      return;
    }

    try {
      const promises = custNosToFetch.map(async (custNo) => {
        try {
          const response = await fetch(`${process.env.REACT_APP_API_BASE_URL}${process.env.REACT_APP_CUSTOMER_ENDPOINT}`, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json'
            },
            body: JSON.stringify({
              custNo: custNo
            })
          });

          const result = await response.json();
          
          if (result.status && result.data) {
            return {
              custNo,
              data: {
                name: result.data.Cust_Name?.trim() || 'ไม่ระบุ',
                taxCode: result.data.TaxCode?.trim() || '-'
              }
            };
          } else {
            return {
              custNo,
              data: { name: 'ไม่พบข้อมูล', taxCode: '-' }
            };
          }
        } catch (error) {
          console.error(`Error fetching customer ${custNo}:`, error);
          return {
            custNo,
            data: { name: 'เกิดข้อผิดพลาด', taxCode: '-' }
          };
        }
      });

      const results = await Promise.all(promises);
      
      // Update customerData state with all results at once
      const newCustomerData = { ...customerData };
      results.forEach(result => {
        newCustomerData[result.custNo] = result.data;
      });
      
      setCustomerData(newCustomerData);
    } catch (error) {
      console.error('Error in fetchAllCustomerData:', error);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100 relative">
      {/* PDF Loading Overlay */}
      {pdfLoading && (
        <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center">
          <div className="bg-white rounded-lg shadow-xl p-8 max-w-md w-full mx-4">
            <div className="text-center">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
              <h3 className="text-lg font-semibold text-gray-900 mb-2">กำลังสร้าง PDF</h3>
              
              {/* Progress Bar */}
              <div className="w-full bg-gray-200 rounded-full h-3 mb-3">
                <div 
                  className="bg-blue-600 h-3 rounded-full transition-all duration-300 ease-out"
                  style={{ width: `${Math.max(0, Math.min(100, pdfProgress.current))}%` }}
                ></div>
              </div>
              
              {/* Progress Message */}
              <p className="text-sm text-gray-600 mb-2">
                {pdfProgress.message || 'กำลังดำเนินการ...'}
              </p>
              
              {/* Progress Percentage */}
              <p className="text-xs text-gray-500 font-mono">
                {Math.round(pdfProgress.current)}% เสร็จสิ้น
              </p>
              
              {/* Customer loading progress removed - now handled by PDF progress popup */}
              
              {/* Debug information removed for cleaner interface */}
              
              <p className="text-xs text-gray-400 mt-4">
                กรุณารอสักครู่ การสร้าง PDF อาจใช้เวลาสักครู่
              </p>
            </div>
          </div>
        </div>
      )}
      {/* Header */}
      <div className="bg-white shadow-sm border-b border-gray-200 px-6 py-4">
        <div className="flex items-center gap-4">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">รายงานการขาย</h1>
            <p className="text-gray-600">รายงานการขาย</p>
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="p-6">
        <div className="max-w-7xl mx-auto">
          {/* Filters */}
          <div className="bg-white rounded-2xl shadow-lg p-6 mb-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
              <Calendar className="w-5 h-5" />
              ตัวกรองข้อมูล
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
              {/* Month Selector */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">เดือน</label>
                <select
                  value={filters.month}
                  onChange={(e) => setFilters(prev => ({ ...prev, month: parseInt(e.target.value) }))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  {monthOptions.map(month => (
                    <option key={month.value} value={month.value}>{month.label}</option>
                  ))}
                </select>
              </div>

              {/* Year Selector */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">ปี</label>
                <select
                  value={filters.year}
                  onChange={(e) => setFilters(prev => ({ ...prev, year: parseInt(e.target.value) }))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  {generateYearOptions().map(year => (
                    <option key={year.value} value={year.value}>{year.label}</option>
                  ))}
                </select>
              </div>

              {/* Prefix Selector */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">ประเภทการขาย</label>
                <select
                  value={filters.prefix}
                  onChange={(e) => setFilters(prev => ({ ...prev, prefix: e.target.value }))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  {prefixOptions.map(prefix => (
                    <option key={prefix.value} value={prefix.value}>{prefix.label}</option>
                  ))}
                </select>
              </div>

              {/* Search Button */}
              <div className="flex items-end">
                <button
                  onClick={handleSearch}
                  disabled={loading}
                  className="w-full bg-blue-600 hover:bg-blue-700 disabled:bg-blue-400 text-white font-medium py-2 px-4 rounded-lg transition-colors flex items-center justify-center gap-2"
                >
                  <Search className="w-4 h-4" />
                  {loading ? 'กำลังค้นหา...' : 'ค้นหา'}
                </button>
              </div>
            </div>
          </div>

          {/* Month/Year Title */}
          <div className="text-center mb-6">
            <h2 className="text-xl font-semibold text-gray-800">
              {monthOptions.find(m => m.value === filters.month)?.label} {filters.year}
            </h2>
          </div>

          {/* Results */}
          <div className="bg-white rounded-2xl shadow-lg overflow-hidden">
            {/* Table Header */}
            <div className="px-6 py-4 border-b border-gray-200 flex justify-between items-center">
              <h3 className="text-lg font-semibold text-gray-900">
                ผลการค้นหา ({pagination.total.toLocaleString()} รายการ)
              </h3>
              <div className="flex flex-col items-end gap-2">
                <div className="flex items-center gap-2">
                  <button 
                    onClick={exportToPDF}
                    disabled={pdfLoading || loading || invoices.length === 0}
                    className="flex items-center gap-2 px-4 py-2 text-blue-600 hover:bg-blue-50 rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    <Download className="w-4 h-4" />
                    {pdfLoading ? 'กำลังสร้าง PDF...' : 'Export PDF'}
                  </button>
                  
                  {Object.keys(customerNameOverrides).length > 0 && (
                    <span className="text-xs text-blue-600 bg-blue-50 px-2 py-1 rounded-full">
                      แก้ไขชื่อ {Object.keys(customerNameOverrides).length} รายการ
                    </span>
                  )}
                  {Object.keys(taxIdOverrides).length > 0 && (
                    <span className="text-xs text-blue-600 bg-blue-50 px-2 py-1 rounded-full">
                      แก้ไขเลขภาษี {Object.keys(taxIdOverrides).length} รายการ
                    </span>
                  )}
                </div>
                
                {/* PDF Export Progress Indicator */}
                {pdfLoading && (
                  <div className="min-w-80 bg-white border border-gray-200 rounded-lg shadow-lg p-4">
                    <div className="flex items-center gap-3 mb-3">
                      <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-blue-600"></div>
                      <span className="text-sm font-medium text-gray-700">กำลังสร้าง PDF</span>
                    </div>
                    
                    {/* Progress Bar */}
                    <div className="w-full bg-gray-200 rounded-full h-2 mb-2">
                      <div 
                        className="bg-blue-600 h-2 rounded-full transition-all duration-300 ease-out"
                        style={{ width: `${Math.max(0, Math.min(100, pdfProgress.current))}%` }}
                      ></div>
                    </div>
                    
                    {/* Progress Details */}
                    <div className="flex justify-between items-center text-xs text-gray-600">
                      <span>{pdfProgress.message || 'กำลังดำเนินการ...'}</span>
                      <span>{Math.round(pdfProgress.current)}%</span>
                    </div>
                    
                      </div>
                )}
              </div>
            </div>

            {/* Summary Totals Section */}
            {invoices.length > 0 && (
              <div className="px-6 py-4 bg-blue-50 border-b border-gray-200">
                <h4 className="text-lg font-semibold text-gray-900 mb-3">สรุปยอดรวม</h4>
                <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                  <div className="bg-white p-4 rounded-lg shadow-sm">
                    <div className="text-sm font-medium text-gray-600 mb-1">จำนวนรายการ</div>
                    <div className="text-2xl font-bold text-gray-900">
                      {summaryTotals.totalRecords.toLocaleString()} รายการ
                    </div>
                  </div>
                  <div className="bg-white p-4 rounded-lg shadow-sm">
                    <div className="text-sm font-medium text-gray-600 mb-1">มูลค่าสินค้าหรือบริการ</div>
                    <div className="text-2xl font-bold text-blue-600">
                      ฿{formatNumber(summaryTotals.totalBeforeVat)}
                    </div>
                  </div>
                  <div className="bg-white p-4 rounded-lg shadow-sm">
                    <div className="text-sm font-medium text-gray-600 mb-1">ภาษีมูลค่าเพิ่ม</div>
                    <div className="text-2xl font-bold text-orange-600">
                      ฿{formatNumber(summaryTotals.totalVat)}
                    </div>
                  </div>
                  <div className="bg-white p-4 rounded-lg shadow-sm">
                    <div className="text-sm font-medium text-gray-600 mb-1">รวมเงินทั้งสิ้น</div>
                    <div className="text-2xl font-bold text-green-600">
                      ฿{formatNumber(summaryTotals.grandTotal)}
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Table */}
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">ลำดับ</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">วันที่</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">เลขที่</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">ชื่อผู้ซื้อสินค้า/ผู้รับบริการ</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">เลขประจำตัวผู้เสียภาษี</th>
                    <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider" dangerouslySetInnerHTML={{__html: 'มูลค่าสินค้า<br/>หรือบริการ'}}></th>
                    <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider" dangerouslySetInnerHTML={{__html: 'จำนวนเงิน<br/>ภาษีมูลค่าเพิ่ม'}}></th>
                    <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">รวมทั้งสิ้น</th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {loading ? (
                    <tr>
                      <td colSpan="8" className="px-6 py-16 text-center">
                        <div className="flex flex-col items-center justify-center space-y-4 max-w-sm mx-auto">
                          <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-blue-600"></div>
                          <span className="text-gray-700 font-medium">{loadingStep.message || 'กำลังโหลดข้อมูล...'}</span>
                          <div className="w-full bg-gray-200 rounded-full h-2">
                            <div
                              className="bg-blue-600 h-2 rounded-full transition-all duration-500"
                              style={{ width: `${loadingStep.progress}%` }}
                            ></div>
                          </div>
                          <span className="text-xs text-gray-500">{loadingStep.progress}%</span>
                        </div>
                      </td>
                    </tr>
                  ) : invoices.length === 0 ? (
                    <tr>
                      <td colSpan="8" className="px-6 py-12 text-center text-gray-500">
                        ไม่พบข้อมูล
                      </td>
                    </tr>
                  ) : (
                    invoices.map((invoice, index) => {
                      const rowNumber = (pagination.page - 1) * pagination.limit + index + 1;
                      const beforeVat = calculateBeforeVat(invoice.TotalAmount, invoice.Vat_Amount);
                      const custNo = invoice.Cust_No?.trim();
                      
                      // Get customer data with special handling for cash invoices
                      let customer = customerData[custNo];
                      const isCashInvoice = invoice.Type_Inv_Code === "1";
                      const hasBillPrintType = invoice.Bill_Print_Type === true;
                      
                      // PRIORITY 1: หากไม่มีเลขบัตรประจำตัวผู้เสียภาษี (หรือเป็น '-') ให้แสดง เงินสด ทันที
                      if (!customer || !customer.taxCode || customer.taxCode === '-' || customer.taxCode.trim() === '') {
                        customer = { name: 'เงินสด', taxCode: '-', branchNo: 0 };
                        console.log(`🟡 SCREEN NO TAX ID: ${invoice.Inv_Number} -> เงินสด (no valid taxCode)`);
                      }
                      // PRIORITY 2: หากมีเลขบัตรประจำตัวผู้เสียภาษี ให้เข้าเงื่อนไขการทำงานเหมือนเดิม
                      else {
                        // Force cash display if it's a cash invoice with Bill_Print_Type: true
                        if (isCashInvoice && hasBillPrintType) {
                          customer = { name: 'เงินสด', taxCode: '-', branchNo: 0 };
                          console.log(`🟡 SCREEN CASH INVOICE with Bill_Print_Type: ${invoice.Inv_Number} -> เงินสด (forced by Bill_Print_Type: true)`);
                        }
                        // If customer name is 'ไม่ระบุ' and it's cash invoice
                        else if (customer.name === 'ไม่ระบุ' && isCashInvoice) {
                          customer = { name: 'เงินสด', taxCode: '-', branchNo: 0 };
                        }
                      }
                      
                      // Check user permission for styling
                      const storedUserData = localStorage.getItem('userData');
                      const currentUser = storedUserData ? JSON.parse(storedUserData) : null;
                      const userName = currentUser?.username || '';
                      const canViewDetails = userName.toLowerCase() === 'pensri';
                      
                      const isCreditNote = invoice.type === 'credit_note';
                      const cnType = invoice.cnType; // type1, type2, type3, unknown
                      
                      // กำหนดสีตามประเภท CN
                      let bgColor = 'hover:bg-gray-50';
                      let textColor = 'text-gray-900';
                      
                      if (isCreditNote) {
                        switch (cnType) {
                          case 'type1':
                            bgColor = 'hover:bg-blue-50 bg-blue-50';
                            textColor = 'text-blue-600';
                            break;
                          case 'type2':
                            bgColor = 'hover:bg-red-50 bg-red-50';
                            textColor = 'text-red-600';
                            break;
                          case 'type3':
                            bgColor = 'hover:bg-green-50 bg-green-50';
                            textColor = 'text-green-600';
                            break;
                          default:
                            bgColor = 'hover:bg-gray-50 bg-gray-50';
                            textColor = 'text-gray-600';
                        }
                      }
                      
                      const rowClass = `${bgColor} ${canViewDetails ? 'cursor-pointer' : 'cursor-default'}`;
                      const textClass = textColor;
                      
                      return (
                        <tr 
                          key={invoice.displayNumber || invoice.Inv_Number} 
                          className={rowClass}
                          onClick={() => handleRowClick(invoice)}
                        >
                          <td className={`px-6 py-4 whitespace-nowrap text-sm ${textClass}`}>
                            {rowNumber}
                          </td>
                          <td className={`px-6 py-4 whitespace-nowrap text-sm ${textClass}`}>
                            {formatDate(invoice.displayDate || invoice.Inv_Date)}
                          </td>
                          <td className={`px-6 py-4 whitespace-nowrap text-sm font-medium ${textClass}`}>
                            {isCreditNote
                              ? (
                                <span title={`Credit Note (เกี่ยวข้องกับ Invoice: ${invoice.relatedInvoice})`}>
                                  {invoice.displayNumber}
                                </span>
                              )
                              : formatInvoiceNumber(invoice)
                            }
                          </td>
                          <td className={`px-6 py-4 text-sm ${textClass}`} onClick={(e) => e.stopPropagation()}>
                            {(() => {
                              const invoiceKey = invoice.displayNumber || invoice.Inv_Number;
                              const displayName = customerNameOverrides[invoiceKey] || customer.name;
                              const isEdited = !!customerNameOverrides[invoiceKey];

                              if (editingRow === invoiceKey) {
                                return (
                                  <div className="flex items-center gap-1">
                                    <input
                                      ref={editInputRef}
                                      type="text"
                                      value={editingValue}
                                      onChange={(e) => setEditingValue(e.target.value)}
                                      onKeyDown={(e) => {
                                        if (e.key === 'Enter') handleConfirmEdit(invoiceKey);
                                        if (e.key === 'Escape') handleCancelEdit();
                                      }}
                                      className="border border-blue-400 rounded px-2 py-1 text-sm w-full focus:outline-none focus:ring-2 focus:ring-blue-300"
                                      autoFocus
                                    />
                                    <button onClick={() => handleConfirmEdit(invoiceKey)} className="text-green-600 hover:text-green-800 p-1" title="ยืนยัน">
                                      <Check className="w-4 h-4" />
                                    </button>
                                    <button onClick={handleCancelEdit} className="text-red-500 hover:text-red-700 p-1" title="ยกเลิก">
                                      <X className="w-4 h-4" />
                                    </button>
                                  </div>
                                );
                              }

                              return (
                                <div className="flex items-center gap-1 group">
                                  <span className={`truncate max-w-xs ${isEdited ? 'text-blue-600 font-medium' : ''}`} title={displayName}>
                                    {displayName}
                                  </span>
                                  {isEdited && <span className="text-xs text-blue-400" title="แก้ไขแล้ว">✎</span>}
                                  <button
                                    onClick={(e) => handleStartEdit(invoiceKey, displayName, e)}
                                    className="opacity-0 group-hover:opacity-100 text-gray-400 hover:text-blue-600 p-1 transition-opacity"
                                    title="แก้ไขชื่อผู้ซื้อ"
                                  >
                                    <Pencil className="w-3 h-3" />
                                  </button>
                                </div>
                              );
                            })()}
                          </td>
                          <td className={`px-6 py-4 whitespace-nowrap text-sm ${textClass}`} onClick={(e) => e.stopPropagation()}>
                            {(() => {
                              const invoiceKey = invoice.displayNumber || invoice.Inv_Number;
                              const displayTaxId = taxIdOverrides[invoiceKey] || customer.taxCode;
                              const isTaxIdEdited = !!taxIdOverrides[invoiceKey];

                              if (editingTaxIdRow === invoiceKey) {
                                return (
                                  <div className="flex items-center gap-1">
                                    <input
                                      ref={editTaxIdInputRef}
                                      type="text"
                                      value={editingTaxIdValue}
                                      onChange={(e) => setEditingTaxIdValue(e.target.value)}
                                      onKeyDown={(e) => {
                                        if (e.key === 'Enter') handleConfirmEditTaxId(invoiceKey);
                                        if (e.key === 'Escape') handleCancelEditTaxId();
                                      }}
                                      className="border border-blue-400 rounded px-2 py-1 text-sm w-full focus:outline-none focus:ring-2 focus:ring-blue-300"
                                      autoFocus
                                    />
                                    <button onClick={() => handleConfirmEditTaxId(invoiceKey)} className="text-green-600 hover:text-green-800 p-1" title="ยืนยัน">
                                      <Check className="w-4 h-4" />
                                    </button>
                                    <button onClick={handleCancelEditTaxId} className="text-red-500 hover:text-red-700 p-1" title="ยกเลิก">
                                      <X className="w-4 h-4" />
                                    </button>
                                  </div>
                                );
                              }

                              return (
                                <div className="flex items-center gap-1 group">
                                  <span className={`${isTaxIdEdited ? 'text-blue-600 font-medium' : ''}`} title={displayTaxId}>
                                    {displayTaxId}
                                  </span>
                                  {isTaxIdEdited && <span className="text-xs text-blue-400" title="แก้ไขแล้ว">✎</span>}
                                  <button
                                    onClick={(e) => handleStartEditTaxId(invoiceKey, displayTaxId, e)}
                                    className="opacity-0 group-hover:opacity-100 text-gray-400 hover:text-blue-600 p-1 transition-opacity"
                                    title="แก้ไขเลขประจำตัวผู้เสียภาษี"
                                  >
                                    <Pencil className="w-3 h-3" />
                                  </button>
                                </div>
                              );
                            })()}
                          </td>
                          <td className={`px-6 py-4 whitespace-nowrap text-sm ${textClass} text-right`}>
                            {formatNumber(beforeVat)}
                          </td>
                          <td className={`px-6 py-4 whitespace-nowrap text-sm ${textClass} text-right`}>
                            {formatNumber(invoice.Vat_Amount)}
                          </td>
                          <td className={`px-6 py-4 whitespace-nowrap text-sm font-medium ${textClass} text-right`}>
                            {formatNumber(invoice.TotalAmount)}
                          </td>
                        </tr>
                      );
                    })
                  )}
                </tbody>
              </table>
            </div>

            {/* Pagination */}
            {pagination.pages > 1 && (
              <div className="px-6 py-4 border-t border-gray-200 flex items-center justify-between">
                <div className="text-sm text-gray-700">
                  แสดง {((pagination.page - 1) * pagination.limit) + 1} - {Math.min(pagination.page * pagination.limit, pagination.total)} จาก {pagination.total.toLocaleString()} รายการ
                </div>
                <div className="flex items-center gap-2">
                  {/* First Page Button */}
                  <button
                    onClick={() => handlePageChange(1)}
                    disabled={pagination.page === 1}
                    className="px-3 py-1 text-sm text-gray-600 hover:bg-gray-100 rounded-lg disabled:opacity-50 disabled:hover:bg-transparent"
                    title="หน้าแรก"
                  >
                    หน้าแรก
                  </button>
                  
                  <button
                    onClick={() => handlePageChange(pagination.page - 1)}
                    disabled={pagination.page === 1}
                    className="p-2 text-gray-600 hover:bg-gray-100 rounded-lg disabled:opacity-50 disabled:hover:bg-transparent"
                  >
                    <ChevronLeft className="w-4 h-4" />
                  </button>
                  
                  <div className="flex gap-1">
                    {Array.from({ length: Math.min(5, pagination.pages) }, (_, i) => {
                      let pageNum;
                      if (pagination.pages <= 5) {
                        pageNum = i + 1;
                      } else {
                        if (pagination.page <= 3) {
                          pageNum = i + 1;
                        } else if (pagination.page >= pagination.pages - 2) {
                          pageNum = pagination.pages - 4 + i;
                        } else {
                          pageNum = pagination.page - 2 + i;
                        }
                      }
                      
                      return (
                        <button
                          key={pageNum}
                          onClick={() => handlePageChange(pageNum)}
                          className={`px-3 py-1 text-sm rounded transition-colors ${
                            pageNum === pagination.page
                              ? 'bg-blue-600 text-white'
                              : 'text-gray-600 hover:bg-gray-100'
                          }`}
                        >
                          {pageNum}
                        </button>
                      );
                    })}
                  </div>

                  <button
                    onClick={() => handlePageChange(pagination.page + 1)}
                    disabled={pagination.page === pagination.pages}
                    className="p-2 text-gray-600 hover:bg-gray-100 rounded-lg disabled:opacity-50 disabled:hover:bg-transparent"
                  >
                    <ChevronRight className="w-4 h-4" />
                  </button>
                  
                  {/* Last Page Button */}
                  <button
                    onClick={() => handlePageChange(pagination.pages)}
                    disabled={pagination.page === pagination.pages}
                    className="px-3 py-1 text-sm text-gray-600 hover:bg-gray-100 rounded-lg disabled:opacity-50 disabled:hover:bg-transparent"
                    title="หน้าสุดท้าย"
                  >
                    หน้าสุดท้าย
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Invoice Details Popup */}
      {showInvoiceDetails && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-xl max-w-4xl w-full max-h-[80vh] overflow-hidden">
            {/* Header */}
            <div className="px-6 py-4 border-b border-gray-200 flex justify-between items-center">
              <h3 className="text-lg font-semibold text-gray-900">
                รายละเอียดใบแจ้งหนี้: {selectedInvoice?.Inv_Number}
              </h3>
              <button
                onClick={() => setShowInvoiceDetails(false)}
                className="text-gray-400 hover:text-gray-600 transition-colors"
              >
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>

            {/* Content */}
            <div className="p-6 overflow-auto max-h-[60vh]">
              {loadingDetails ? (
                <div className="flex items-center justify-center py-12">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
                  <span className="ml-2 text-gray-600">กำลังโหลดข้อมูล...</span>
                </div>
              ) : invoiceDetails.length === 0 ? (
                <div className="text-center py-12 text-gray-500">
                  ไม่พบข้อมูลรายละเอียด
                </div>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full border-collapse">
                    <thead>
                      <tr className="bg-gray-50">
                        <th className="border border-gray-300 px-4 py-2 text-left text-sm font-medium text-gray-700">ลำดับ</th>
                        <th className="border border-gray-300 px-4 py-2 text-left text-sm font-medium text-gray-700">รหัสสินค้า</th>
                        <th className="border border-gray-300 px-4 py-2 text-left text-sm font-medium text-gray-700">รายละเอียด</th>
                        <th className="border border-gray-300 px-4 py-2 text-right text-sm font-medium text-gray-700">จำนวน</th>
                        <th className="border border-gray-300 px-4 py-2 text-right text-sm font-medium text-gray-700">ราคาต่อหน่วย</th>
                        <th className="border border-gray-300 px-4 py-2 text-right text-sm font-medium text-gray-700">รวมทั้งสิ้น</th>
                      </tr>
                    </thead>
                    <tbody>
                      {invoiceDetails.map((detail, index) => (
                        <tr key={index} className={index % 2 === 0 ? 'bg-white' : 'bg-gray-50'}>
                          <td className="border border-gray-300 px-4 py-2 text-sm text-gray-900">
                            {detail.Seq_Number}
                          </td>
                          <td className="border border-gray-300 px-4 py-2 text-sm text-gray-900">
                            {detail.Item_Number?.trim()}
                          </td>
                          <td className="border border-gray-300 px-4 py-2 text-sm text-gray-900">
                            {detail.InvDet_Desc}
                          </td>
                          <td className="border border-gray-300 px-4 py-2 text-sm text-gray-900 text-right">
                            {detail.Order_Qty?.toLocaleString()}
                          </td>
                          <td className="border border-gray-300 px-4 py-2 text-sm text-gray-900 text-right">
                            {detail.Price?.toLocaleString('th-TH', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                          </td>
                          <td className="border border-gray-300 px-4 py-2 text-sm text-gray-900 text-right font-medium">
                            {((detail.Price || 0) * (detail.Order_Qty || 0)).toLocaleString('th-TH', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>

            {/* Footer */}
            <div className="px-6 py-4 border-t border-gray-200 flex justify-end">
              <button
                onClick={() => setShowInvoiceDetails(false)}
                className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-lg transition-colors"
              >
                ปิด
              </button>
            </div>
          </div>
        </div>
      )}

      {/* PDF Export Progress Popup */}
      {pdfProgress.show && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl shadow-2xl max-w-md w-full p-6">
            <div className="text-center">
              <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <Download className="w-8 h-8 text-blue-600" />
              </div>
              
              <h3 className="text-lg font-semibold text-gray-900 mb-2">กำลังสร้าง PDF</h3>
              
              {/* Progress Bar */}
              <div className="w-full bg-gray-200 rounded-full h-3 mb-4">
                <div 
                  className="bg-blue-600 h-3 rounded-full transition-all duration-300 ease-out"
                  style={{ width: `${pdfProgress.percentage}%` }}
                ></div>
              </div>
              
              {/* Progress Text */}
              <div className="space-y-2">
                <p className="text-sm text-gray-600">{pdfProgress.step}</p>
                <p className="text-xs text-gray-500">{Math.round(pdfProgress.percentage)}%</p>
              </div>
              
              {/* Loading Animation */}
              {pdfProgress.percentage < 100 && !pdfProgress.step.includes('❌') && (
                <div className="mt-4 flex justify-center">
                  <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-blue-600"></div>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default InvoiceReportComponent; 
